/** @jsx jsx */
import {
  BaseButton,
  Button,
  Grid,
  Icon,
  Spacer,
  Spinner,
  TextGroupMedium,
  useMediaQuery,
} from "@cd2/components"
import { FilePreview, FileUploader, useFileboy } from "@cd2/fileboy-react"
import { Fields, Form2 } from "@cd2/forms"
import { IWebClient } from "@cd2/skeem-client"
import { H4, P, TextBlock } from "@cd2/text/build/es6"
import { doo, IComponentCallback, IxRefFor, pluralize, titleize } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import { differenceInSeconds } from "date-fns"
import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useHistory } from "react-router-dom"
import { fortusEwBranchId } from "../../../shared/constants"
import { Checkbox } from "../../Components/2023FormFields/Checkbox"
import { ButtonsWrapper } from "../../Components/ButtonsWrapper"
import { ConfirmCancelDialogue } from "../../Components/ConfirmCancelDialogue"
import { DebugPanel } from "../../Components/DebugPanel"
import { brandColors, colors, themeColors } from "../../Components/EOSThemeProvider"
import { IEventModel, useEvents } from "../../Components/EventsList"
import { FieldWithDescription } from "../../Components/FieldWithDescription"
import { FieldsRadioButtonSelect } from "../../Components/FieldsRadioCheckbox"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { useNotification } from "../../Components/NotificationsBell"
import { PageWrapper } from "../../Components/PageWrapper"
import { AddressesSectionAsForm } from "../../Components/Pages/CustomerShowPage/AddressesSectionAsForm"
import { PreventPageNavigation } from "../../Components/PreventPageNavigation"
import { QuickToolTip } from "../../Components/QuickTooltip"
import { NoResults } from "../../Components/TableWrapper"
import { Toolbar } from "../../Components/Toolbar"
import { AlertBanners } from "../../Pages/AlertBanners"
import { useJobComments } from "../../Pages/Jobs/JobsShowPageComponent"
import { jobStatusTypes } from "../../Pages/Jobs/jobStatusTypes"
import { orderRequestStatusTypes } from "../../Pages/OrderRequests/orderRequestStatusTypes"
import { buttonConfigs } from "../../constants/buttonConfigs"
import { icons } from "../../constants/icons"
import { useProductGroupProductPrice } from "../../hooks/productGroupProductPrice"
import { IProductLocationStockResponseItem, useApi } from "../../hooks/useApi"
import { useAsyncCallback } from "../../hooks/useAsyncCallback"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useConfirmation } from "../../hooks/useConfirmation"
import { useInterval } from "../../hooks/useInterval"
import { useProjectFromQuote } from "../../hooks/useProjectFromQuote"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { useWatchState } from "../../hooks/useWatchState"
import { IRecord } from "../../types/schema"
import { fuzzyNumberCompare, sum } from "../../utils/arrays"
import { convertPriceCurrency, currencyNameToSymbol, isCurrencyMatch } from "../../utils/currency"
import { getProductLocationPrioritized } from "../../utils/getProductLocations"
import { getProductSuggestedPrice } from "../../utils/getProductSuggestedPrice"
import { getTotals } from "../../utils/getQuoteOrderTotals"
import {
  isSplitPaymentMethod,
  paymentAmountField,
  paymentMethodParts,
} from "../../utils/paymentDetails"
import { IBranchSettings, useSettings } from "../../utils/useSettings"
import { shouldResetBypassMinMarginApproved } from "../../utils/minMarginPriceBypass"
import { calculateBuildQuantity, formatPrice, isTempId } from "../../utils/utils"
import { CommercialInvoiceInput } from "./CommercialInvoiceInput"
import { CustomerPanel } from "./CustomerPanel"
import {
  calculateCostPriceFromCurrency,
  IProductForCalculateCostPriceFromCurrency,
  productAttrsForCalculateCostPriceFromCurrency,
} from "./ItemRowSageProduct"
import { ItemTable } from "./ItemTable"
import { SaveButtons } from "./SaveButtons"
import { SaveWarningModal } from "./SaveWarningModal"
import {
  calculateDiscountPercent,
  calculateMarginFromPrice,
  filterDeleted,
  getProductItemChildren,
  IAddress,
  ICustomerHeadingFieldsProps,
  IDeletedItem,
  IItem,
  IQuoteItemRegisterOptions,
  IQuoteOrderRecord,
  isDeleted,
  itemIsChild,
  itemIsChildOf,
  itemTypes,
  IValidationResult,
  scrollToTopMostError,
  getOrderTotalPennies,
} from "./quoteOrderUtils"
import { useAlertMinimumMargin } from "./useAlertMinimumMargin"
import { useConfirmCostPriceUpdate } from "./useConfirmCostPriceUpdate"
import { useLocalStorage } from "../../hooks/useLocalStorage"
import { validateAddressFieldsLength } from "../../utils/validateAddressFieldsLength"
import { getSageAddressLengthErrors } from "../../utils/validateSageAddressLength"
import { summariseCustomerPrices } from "../../utils/summariseCustomerPrices"
import { da } from "date-fns/locale"

const defaultNewRecord = {
  items: [],
  address: {},
  files: [],
}

export interface IAfterSaveOpts {
  savedRecord: IQuoteOrderRecord
  shouldPreview: boolean
  setRecord: (record: IQuoteOrderRecord) => void
}

interface IQuoteOrderFormProps {
  type: "order" | "quote"
  title: string
  record: IQuoteOrderRecord
  CustomerHeadingFields: (props: ICustomerHeadingFieldsProps) => JSX.Element
  isNewPage?: boolean
  startWithBillingAddressUnchecked?: boolean
  disableCustomerSelect?: boolean
  directDeliverySourceOrder?: IRecord<"orders"> & { branch?: IRecord<"branches", "name"> }

  afterSave(opts: IAfterSaveOpts): Promise<void> | void

  // edit the data to save prior to it being transformed into a mutation
  tapDataBeforeMutate?(data: IQuoteOrderRecord): Promise<IQuoteOrderRecord>

  // edit the mutation after it has been created
  tapMutationBuilding?(mutation: any, data: IQuoteOrderRecord): any
  shipToCustomerOrder?: boolean
}

const numberOfFailures = 0

export function checkAddressHasData(
  address?: Partial<
    IRecord<
      "addresses",
      "city" | "county" | "postcode" | "name" | "line_1" | "line_2" | "line_3" | "line_4"
    >
  >,
) {
  const keys = [
    "city",
    "county",
    "postcode",
    "name",
    "line_1",
    "line_2",
    "line_3",
    "line_4",
  ] as (keyof typeof address)[]
  return keys.some(key => !!address?.[key])
}

interface ICache {
  key: string
  state: any
  date: string
  type: string
}

function useCacheState(uid: string, type: string, setState: (data: any) => void, record: any) {
  const history = useHistory()
  const shouldCacheRef = useRef(false)
  const cacheKey = "quoteOrderFormCache"
  const stateRef = useRef(record)
  stateRef.current = record

  function cacheState() {
    if (!stateRef.current || !shouldCacheRef.current) {
      return
    }
    console.log("CACHING STATE", "=>", uid)
    window.localStorage.setItem(
      cacheKey,
      JSON.stringify({ state: stateRef.current, key: uid, date: new Date(), type }),
    )
  }

  function getCache() {
    try {
      const state = window.localStorage.getItem(cacheKey)
      if (!state) {
        return null
      }
      const parsedState = JSON.parse(state ?? "{}") as ICache
      const isValidState =
        !!parsedState.state &&
        parsedState.key === uid &&
        parsedState.type === type &&
        differenceInSeconds(new Date(), new Date(parsedState.date)) < 5 * 60

      return isValidState ? parsedState.state : null
    } catch (_) {
      return null
    }
  }

  function loadFromCache() {
    const urlParams = new URLSearchParams(window.location.search)
    // Prevent the restore cache state popping up when
    // - creating quotes + (check type == order)
    // - orders from requests +? (check order request id present)
    // - jobs + (check job id)
    // - new from customer show (check customer ID in url)
    const isFromCustomerShow = urlParams.has("customerId")
    const isOrder = type === "order"
    const isFromJob = !!record.job?.id
    const isFromOrderRequest = !!record.orderRequest?.id

    const canCache = isOrder && !isFromJob && !isFromOrderRequest && !isFromCustomerShow
    if (!isOrder) {
      console.log("Cache disabled - not an order")
    }
    if (isFromJob) {
      console.log("Cache disabled - from job")
    }
    if (isFromOrderRequest) {
      console.log("Cache disabled - from order request")
    }
    if (isFromCustomerShow) {
      console.log("Cache disabled - from customer show")
    }
    if (!canCache) {
      shouldCacheRef.current = false
      console.log("Can cache:", canCache)
      return
    }
    const state = getCache()
    if (state) {
      handleConfirmRestore()
        .then(result => {
          if (result) {
            setState(state)
          }
        })
        .finally(() => {
          shouldCacheRef.current = true
          console.log("Caching enabled:", shouldCacheRef.current)
        })
    } else {
      shouldCacheRef.current = true
      console.log("Caching enabled:", shouldCacheRef.current)
    }
  }

  function clearCacheState() {
    console.log("CLEARING CACHE")
    window.localStorage.removeItem(cacheKey)
  }

  const [restoreModal, handleConfirmRestore] = useConfirmation<void, boolean>(({ onResolve }) => (
    <ConfirmCancelDialogue
      heading={`A cached state for a ${type} was found.`}
      body={
        <React.Fragment>
          <P>Do you want to restore this {type}?</P>
          <P>This will overwrite current data with the cached state.</P>
        </React.Fragment>
      }
      onCancel={() => onResolve(false)}
      onConfirm={() => onResolve(true)}
      isOpen
    />
  ))

  useInterval(cacheState, 5 * 1000)

  useEffect(() => {
    if (!record) {
      return
    }
    setTimeout(() => {
      // forcing a delay so that any other callbacks going on can finish before we try and restore
      // the state as restoring state too quickly on mount can get overwritten by something ...
      loadFromCache()
    }, 2000)
    history.listen(_ => {
      clearCacheState()
    })
  }, [!!record])

  return { loadFromCache, clearCacheState, useCacheModal: restoreModal }
}

export function QuoteOrderForm({
  type,
  title,
  record: initialRecord,
  CustomerHeadingFields,
  afterSave,
  tapDataBeforeMutate,
  tapMutationBuilding,
  isNewPage,
  startWithBillingAddressUnchecked,
  disableCustomerSelect,
  directDeliverySourceOrder,
  shipToCustomerOrder,
}: IQuoteOrderFormProps) {
  const [isCustomerViewing, setCustomerViewing] = useLocalStorage("customerViewing", false)

  const api = useApi()
  const events = useEvents()
  const isSmall = useMediaQuery({ maxWidth: 850 })
  const { currentUser, currency } = useGlobalState()
  const { barScanOrders } = useSettings()
  const alerts = useSiteAlerts()
  const skeem = window.skeem
  const settings = useSettings()
  const { sendOrderCreatedForOrderRequestNotification } = useNotification()
  const { projectNode, handleOpenProjectNode } = useProjectFromQuote({ includeSkip: true })
  const getGroupCostPrice = useProductGroupProductPrice()

  const [record, _setRecord] = useState(() => ({
    ...defaultNewRecord,
    ...initialRecord,
    items: (
      initialRecord?.items?.map(item => ({
        ...item,
        originalProductId: "$deleted" in item ? undefined : item.product?.id,
      })) ?? defaultNewRecord.items
    ).map(item => ({
      ...item,
      build_quantity:
        !isDeleted(item) && item.type === itemTypes.buildSetItem
          ? calculateBuildQuantity(
              item.quantity,
              (
                initialRecord?.items.find(
                  x => !isDeleted(x) && x.build_set_uid === item.build_set_uid,
                ) as IItem | undefined
              )?.quantity,
            )
          : null,
    })),
  }))
  const { clearCacheState, useCacheModal } = useCacheState(
    initialRecord?.id ?? "new",
    type,
    _setRecord,
    record,
  )

  // recalculate cost prices if currency_name is changed
  useWatchState(async (prevState, currentState, hasMounted) => {
    if (!currentState || isCurrencyMatch(prevState, currentState)) {
      return
    }
    console.log("CURRENCY CHANGE - SYNCING PRICES", prevState, "=>", currentState)
    let hasMadeChanges = false
    const items = [...record.items]
    const productIds = items
      .filter(item => !("$deleted" in item) && item.type === 0 && item.product)
      .map((item: any) => (item as IItem).product!.id)
    console.log("loading products", productIds.length)
    const products = await skeem.fetch2<IProductForCalculateCostPriceFromCurrency[]>({
      model: {
        name: "products",
        attributes: productAttrsForCalculateCostPriceFromCurrency,
        matches: { id: { or: productIds } },
      },
    })

    for (const item of items) {
      if ("$deleted" in item) {
        continue
      }
      if (item.type !== 0) {
        continue
      }
      if (!item.product) {
        continue
      }
      const product = products.find(p => p.id === item.product!.id)

      if (!product) {
        continue
      }
      try {
        console.log("Updating cost price for", item.product.stock_code)
        console.log("loading group price")

        const newCostPrice = calculateCostPriceFromCurrency({
          currentUser,
          currency_name: currentState,
          product: hasMounted
            ? product
            : { ...product, cost_price_pennies: item.cost_price_pennies },
        })

        console.log("cost price change from", item.cost_price_pennies, "=>", newCostPrice)
        item.cost_price_pennies = newCostPrice
        item.markUp = calculateMarginFromPrice(
          newCostPrice ?? undefined,
          item.price_pennies ?? undefined,
        )
        if (settings.useQuoteOrderStandardPricing) {
          console.log("Syncing standard price")
          const pricing = await getProductSuggestedPrice(skeem, record, product.id)
          const standardPriceCurrencyConverted = convertPriceCurrency(
            pricing?.standardPrice ?? null,
            currentUser.branch.euro_pound_exchange_rate,
            pricing?.currency_name,
            record.currency_name,
          )
          const customerPrice = convertPriceCurrency(
            pricing?.customerPrice ?? null,
            currentUser.branch.euro_pound_exchange_rate,
            pricing?.currency_name,
            record.currency_name,
          )
          item.standard_price_pennies = standardPriceCurrencyConverted
          item.standard_discount_percent = calculateDiscountPercent(
            standardPriceCurrencyConverted,
            customerPrice,
          )
        }
        hasMadeChanges = true
      } catch (e) {
        console.error(e)
      }
    }
    if (hasMadeChanges) {
      console.log("Applying changes to record for currency price conversion")
      setRecord({ ...record, items })
    }
  }, record.currency_name)

  const jobComments = useJobComments(record.job?.id, { sendingTo: "customer" })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<any>({})
  const [dirty, setDirty] = useState(false)
  const paymentLocations = settings.paymentLocations ?? []

  const [customBillingAddress, setCustomBillingAddress] = useState(
    !!initialRecord.billing_address || startWithBillingAddressUnchecked,
  )

  const [willCollect, setWillCollect] = useState(!!initialRecord.will_collect)

  // various refs used to track previous version of the po ref and whether or not it has been validated etc
  // to prevent the skeem request from being spammed out any change as opposed to just po ref change
  const previousPurchaseOrderReferenceRef = useRef<string | undefined>("")
  const shouldValidatePurchaseOrderReferenceRef = useRef(false)
  const hasADuplicatePurchaseOrderReferenceRef = useRef(false)

  const setRecord: typeof _setRecord = args => {
    previousPurchaseOrderReferenceRef.current = record.purchase_order_reference

    setDirty(true)
    return _setRecord(args)
  }

  const [confirmationNode, getConfirmation] = useConfirmation(SaveWarningModal)

  const deliveryAddressRef = useRef<IxRefFor<typeof AddressesSectionAsForm>>(null)
  const billingAddressRef = useRef<IxRefFor<typeof AddressesSectionAsForm>>(null)

  function refetchAddresses() {
    deliveryAddressRef.current?.refetch()
    billingAddressRef.current?.refetch()
  }

  function getShouldValidateAddressLength() {
    return !!settings.useAddressLengthValidation
  }

  async function validateForm(
    data: IQuoteOrderRecord,
    { runAsyncValidations }: { runAsyncValidations?: boolean } = {},
  ): Promise<IValidationResult> {
    /** VALIDATIONS
     * errors:
     *
     * customer - presence
     *
     * if custom product is exporting to sage then
     *  unit_price and stock_code are required.
     *
     * negative prices
     * negative quantity
     *
     * ------
     *
     * warnings:
     *
     * customer account on hold
     * customer account at credit limit
     * cost price > quoted price for any sage/custom product row.
     * empty custom text row
     * empty custom product row
     * empty sage row
     *
     */

    let errors: Record<string, any> = {}
    let warnings: string[] = []
    if (!data.currency_name && settings.allowChangingQuoteOrderCurrency) {
      errors.currency_name = "Must select a currency"
    }

    if (
      type === "order" &&
      (!settings.allowDisableAutoOrderRequestEmail || data.autoEmailOrderAcknowledgementOn)
    ) {
      const sendTo = data.contact?.email || data.delivery_note_email
      if (!sendTo) {
        errors.autoEmailOrderAcknowledgementOn =
          "must have a valid email through a selected contact or delivery note email."
      } else if (!data.autoEmailOrderAcknowledgementOn) {
        errors.autoEmailOrderAcknowledgementOn = "must be present"
      }
    }
    if (type === "order" && data.paymentMethod) {
      // the payment details box (and so the location field) is only shown with this setting
      if (settings.useOrderPaymentMethod && paymentLocations.length > 0 && !data.paymentLocation) {
        errors.paymentLocation = "Select where the payment was taken"
      }
      if (isSplitPaymentMethod(data.paymentMethod)) {
        const methods = paymentMethodParts(data.paymentMethod)
        const total = getOrderTotalPennies(data)
        // a blank amount isn't saved, which would drop the order from the payment totals
        if (methods.some(method => data[paymentAmountField(method)] == null)) {
          errors.paymentSplit = "Enter an amount for each payment method (0 if none was paid)"
        } else {
          const paid = methods.reduce(
            (sum, method) => sum + Math.round(data[paymentAmountField(method)] ?? 0),
            0,
          )
          if (paid !== total) {
            errors.paymentSplit = `The payment amounts must add up to the order total of ${(
              total / 100
            ).toFixed(2)}`
          }
        }
      }
    }
    if (data.hasOpportunityCertificate) {
      if (!data.projectRegistrationNumber) {
        errors.projectRegistrationNumber = "must be present"
      }
      if (!data.opportunityCertificateFile) {
        errors.opportunityCertificateFile = "must include the opportunity certificate"
      }
    }
    if (!data.customer && !data.lead) {
      if (type === "quote") {
        errors.customer = "Must select a customer"
        errors.lead = "Must select a lead"
      } else {
        errors.customer = "Required"
      }
    } else {
      if (data.customer?.account_on_hold) {
        if (settings.enableOnHoldOrders || type === "quote") {
          warnings.push("The selected customer account is on hold")
        } else {
          errors.on_hold = true
        }
      }

      if (record.customer?.id && !record.sub_customer?.id) {
        const customer = await skeem.fetch2<IRecord<"customers", "sub_customers">>({
          first: {
            name: "customers",
            attributes: ["sub_customers"],
            matches: { id: record.customer.id },
          },
        })
        if (customer.sub_customers.length > 0) {
          errors.sub_customer = "Required"
        }
      }

      // this is only setting up the warnings. Check the other method for actually creating the credit limit event
      // as we want the ID of the record to do that, so it is run separately (runCreditLimitCheck)
      if (data.customer?.outstanding_balance_pennies && data.customer?.credit_limit_pennies) {
        const limit = data.customer.credit_limit_pennies ?? 0
        const orderValue =
          getTotals((record.items as any) ?? [], record.carriage_pennies, record.customer?.tax_rate)
            ?.totalPricePennies ?? 0
        const outstandingExceptOrder = data.customer.outstanding_balance_pennies
        const currencyName = data.currency_name ?? currentUser.branch.currency_name
        const currency = currencyNameToSymbol(currencyName)
        const clFormatted = formatPrice(limit, { currency, isPennies: true })
        // customer is less than limit but will go over with this included
        if (outstandingExceptOrder < limit && outstandingExceptOrder + orderValue > limit) {
          const limitExceeded = outstandingExceptOrder + orderValue - limit
          const exceededPrice = formatPrice(limitExceeded, { currency, isPennies: true })
          warnings.push(
            `The customer account will go over their credit limit of ${clFormatted} by ${exceededPrice}.`,
          )
        }
        // customer is already over limit
        if (outstandingExceptOrder > limit) {
          const exceededPrice = formatPrice(orderValue, { currency, isPennies: true })
          const totalExceeded = formatPrice(outstandingExceptOrder + orderValue - limit, {
            currency,
            isPennies: true,
          })
          warnings.push(
            `The customer account has already exceeded their credit limit of ${clFormatted}.\nThis will increase that by ${exceededPrice} to a total of ${totalExceeded} over limit.`,
          )
        }
      }
    }
    if (type === "order") {
      if (
        (!settings.barScanOrders || data.warehouseOverride?.disable_barscan) &&
        !data.delivery_method
      ) {
        errors.delivery_method = "Required"
      }
      if (
        settings.barScanOrders &&
        !data.warehouseOverride?.disable_barscan &&
        !data.dpdProduct &&
        !data.isInternalTransfer
      ) {
        errors.dpdProduct = "Required"
      }
      if (settings.orderRequiredDateMandatory && !data.required_date) {
        errors.required_date = "Required"
      }

      let deliveryService = ""
      if (!settings.barScanOrders) {
        deliveryService = data.delivery_method ?? ""
      } else {
        deliveryService = data.dpdProduct?.body ?? ""
      }
      const fieldMaxLength = deliveryService ? 35 : undefined

      const deliveryAddressErrors = {
        // Sage's own field limits apply to anything that will be exported to Sage - an over-long
        // field (the postal name especially, which the courier rules below don't cover) fails the
        // export outright, so catch it while the address is still in front of the user. Internal
        // transfers never reach the Sage order export, so they're exempt.
        ...(data.isInternalTransfer ? undefined : getSageAddressLengthErrors(data.address)),
        // The courier's limits are tighter, but only apply when the branch has opted in.
        ...(fieldMaxLength
          ? validateAddressFieldsLength(data.address, {
              maxLength: fieldMaxLength,
              shouldValidate: getShouldValidateAddressLength(),
            })
          : undefined),
      }
      if (Object.keys(deliveryAddressErrors).length > 0) {
        errors.address = deliveryAddressErrors
      }
    }

    if (data.isInternalTransfer && !data.internal_transfer_to_branch) {
      errors.internal_transfer_to_branch = "Required"
    }

    for (const item of data.items) {
      const options = registeredItems[item.id]
      if (isDeleted(item) || !options || !options.validate) {
        continue
      }

      const quickValidationResults = options.validate(item, data)
      let slowValidationResults: IValidationResult | undefined

      if (runAsyncValidations && options.asyncValidate) {
        slowValidationResults = await options.asyncValidate(item, data)
      }

      for (const results of [quickValidationResults, slowValidationResults]) {
        if (!results) {
          continue
        }
        if (results.errors && Object.keys(results.errors).length > 0) {
          errors = { ...errors, items: { ...(errors.items || {}), [item.id]: results.errors } }
        }

        if (results.warnings) {
          warnings = [...warnings, ...results.warnings]
        }
      }
    }

    if (
      !data.orderRequest &&
      type === "order" &&
      (!data.purchase_order_reference || data.purchase_order_reference.trim() === "")
    ) {
      errors.purchase_order_reference = "Required"
    } else if (data.purchase_order_reference && data.purchase_order_reference.length > 30) {
      errors.purchase_order_reference = "Purchase order reference must be less than 30 characters"
    }
    if (shouldValidatePurchaseOrderReferenceRef.current) {
      if (
        type === "order" &&
        data.customer &&
        data.purchase_order_reference &&
        data.purchase_order_reference.toLowerCase() !== "stock" &&
        data.purchase_order_reference !== previousPurchaseOrderReferenceRef.current
      ) {
        let matchingOrders = await skeem.fetch<{
          identifier: string
          purchase_order_reference: string
        }>({
          orders: {
            attributes: ["identifier", "purchase_order_reference"],
            filter: {
              matches: {
                customer: data.customer.id,
                purchase_order_reference: { like: data.purchase_order_reference },
              },
            },
          },
        })

        // Case insensitivity check
        matchingOrders = matchingOrders.filter(
          order =>
            order.purchase_order_reference.toLowerCase() ===
            data.purchase_order_reference?.toLowerCase(),
        )

        if (data.id) {
          matchingOrders = matchingOrders.filter(order => order.id !== data.id)
        }

        if (matchingOrders.length > 0) {
          hasADuplicatePurchaseOrderReferenceRef.current = true
          if (!data.confirmed_duplicate_reference) {
            errors.purchase_order_reference =
              "Purchase Order Reference is already assigned to a different order with the same customer"
          }
        }

        shouldValidatePurchaseOrderReferenceRef.current = false
      }
    } else if (hasADuplicatePurchaseOrderReferenceRef.current) {
      if (!data.confirmed_duplicate_reference) {
        errors.purchase_order_reference =
          "Purchase Order Reference is already assigned to a different order with the same customer"
      }
    }

    return { errors, warnings }
  }

  async function handleSaveButtonsClick(opts: { preview: boolean }) {
    if (isSubmitting) {
      return
    }
    // Saving before branch settings arrive would apply allocations against the wrong
    // useExplicitAllocations value
    if (!settings._loaded) {
      alerts.genericError("Settings are still loading, please try again in a moment")
      return
    }
    setIsSubmitting(true)
    const response = await handleSave()
    if (response.status === "error") {
      alerts.genericError(
        response.message
          ? response.message
          : `${pluralize(response.errorCount, "error")} saving ${type}`,
      )
      setIsSubmitting(false)
      return
    }
    clearCacheState()

    if (response.status === "cancel") {
      setIsSubmitting(false)
      return
    }

    const preview = response.legacyPreview !== undefined ? response.legacyPreview : opts.preview
    // setDirty here both before save, incase they want to redirect
    // and after incase the reload a new record
    setDirty(false)
    await afterSave({ savedRecord: response.savedRecord, shouldPreview: preview, setRecord })
    setDirty(false)
    setIsSubmitting(false)
  }

  const [alertBelowMinimumMargin, handleAlertBelowMinimumMargin] = useAlertMinimumMargin()

  const { directDeliveryLocationCheckNode, handleWarnDirectDeliveryLocation } =
    useDirectDeliveryLocationsCheck()

  async function handleSave(): Promise<
    | {
        status: "error"
        errorCount: number
        message?: string
      }
    | {
        status: "cancel"
      }
    | {
        status: "success"
        legacyPreview?: boolean
        savedRecord: IQuoteOrderRecord
      }
  > {
    if (record.customer?.id && !record.sub_customer?.id) {
      const customer = await skeem.fetch2<IRecord<"customers", "sub_customers">>({
        first: {
          name: "customers",
          attributes: ["sub_customers"],
          matches: { id: record.customer.id },
        },
      })
      if (customer.sub_customers.length > 0) {
        return Promise.resolve({
          status: "error",
          errorCount: 1,
          message: "Sub customer is required",
        })
      }
    }

    const isDirectDelivery =
      type === "order" &&
      settings.directDeliveryWarehouse &&
      (record as any)?.delivery_method &&
      ["direct delivery", "direct_delivery", "direct deliveries"].includes(
        (record as any).delivery_method.toLowerCase(),
      )
    if (isDirectDelivery) {
      const confirmed = await handleWarnDirectDeliveryLocation({
        directDeliveryWarehouse: settings.directDeliveryWarehouse!,
        items: record.items.filter(item => !("$deleted" in item)) as IItem[],
      })
      if (!confirmed) {
        return Promise.resolve({ status: "cancel" })
      }
    }
    const total =
      getTotals((record.items as any) ?? [], record.carriage_pennies, record.customer?.tax_rate) ??
      0
    let projectData: any = undefined
    // default to true if type is quote & new record
    let useProject = type === "quote" && !record.id
    if (type === "quote" && record.id) {
      // Load quote project to see if has one already to skip if it does
      const quote = await skeem.fetch2<IRecord<"quotes"> & { project: IRecord<"projects"> }>({
        first: {
          name: "quotes",
          attributes: [{ as: "project", value: { first: { attr: "projects" } } }],
          filter: { matches: { id: record.id } },
        },
      })
      if (!quote.project) {
        useProject = true
      }
    }
    // if quote && doesn't have a project and value > 10k
    let postSaveCallback = (_id: string) => Promise.resolve()

    if (
      type === "quote" &&
      useProject &&
      total.totalPricePennies / 100 > settings.quoteCreateProjectThreshold &&
      currentUser.canViewCRM &&
      record.customer
    ) {
      const customer = await skeem.fetch2<
        IRecord<"customers"> & {
          sales_rep?: IRecord<"sales_reps"> & { user?: IRecord<"skeem_users"> }
        }
      >({
        first: {
          name: "customers",
          attributes: [{ name: "sales_rep", attributes: ["user"] }],
          filter: { matches: { id: record.customer?.id } },
        },
      })
      const result = await handleOpenProjectNode(
        { customer: customer },
        {
          header: "Please choose or create a project for this quote",
          body: (
            <P>
              You can choose to create a new project for this quote or add it to an existing one
              using the form below.
            </P>
          ),
        },
      )
      if (!result) {
        return Promise.resolve({ status: "cancel" })
      }
      if (result.callback) {
        postSaveCallback = result.callback
      }
      // Result is a skeem mutation type value, of either {create:projectData} or {add: projectId}
      projectData = result.query
    }

    if (record.orderRequest?.id) {
      const orderRequest = await skeem.fetch2<
        IRecord<"orderRequests", "order" | "autoExportChecked" | "received_by_branch">
      >({
        first: {
          name: "orderRequests",
          attributes: ["order", "autoExportChecked", "received_by_branch"],
          filter: { matches: { id: record.orderRequest?.id } },
        },
      })
      if (!record.id && orderRequest.order) {
        return {
          status: "error",
          errorCount: 1,
          message: "There is already and order associated to this order request.",
        }
      }
      const now = new Date()
      const minTime = 9
      const maxTime = 17

      const inExportHours = now.getHours() > minTime && now.getHours() < maxTime
      if (
        inExportHours &&
        !record.id &&
        orderRequest.received_by_branch?.id === fortusEwBranchId &&
        !orderRequest.autoExportChecked
      ) {
        return {
          status: "error",
          errorCount: 1,
          message: "This order request is being processed for auto export.",
        }
      }
    }
    try {
      // Do not run minimum margin alerts for internal transfers
      const isInternal =
        "internal_transfer_to_branch" in record && !!record.internal_transfer_to_branch
      if (typeof settings.minimumLineItemMargin === "number" && !isInternal) {
        const minMargin = settings.minimumLineItemMargin
        const invalidItems = filterDeleted(record.items).filter(
          item => item.markUp && item.markUp < minMargin,
        )
        if (invalidItems.length > 0) {
          const alertBelowMinimumMargin = await handleAlertBelowMinimumMargin({
            items: invalidItems,
            minMargin,
            status: currentUser.canSetPricesBelowMinimumMargins ? "warning" : "error",
          })
          if (!currentUser.canSetPricesBelowMinimumMargins) {
            return {
              status: "error",
              errorCount: invalidItems.length,
              message: "Cannot save items below minimum margin.",
            }
          }
          if (!alertBelowMinimumMargin) {
            return { status: "cancel" }
          }
        }
      }
      const job = record.job
      const orderRequest = record.orderRequest

      let dataToSave: IQuoteOrderRecord = {
        ...record,
        projects: projectData,
        items: [...record.items],
        address: { ...record.address },
        billing_address: record.billing_address?.line_1 ? { ...record.billing_address } : undefined,
        job: undefined,
        orderRequest: undefined,
        will_collect: willCollect,
        notes: record.notes,
      }

      if (!barScanOrders || record.warehouseOverride?.disable_barscan) {
        delete dataToSave.dpdProduct
      }

      const validationResults = await validateForm(
        { ...dataToSave, orderRequest: record.orderRequest },
        { runAsyncValidations: true },
      )

      if (validationResults.errors && Object.keys(validationResults.errors).length > 0) {
        setErrors(validationResults.errors)

        // Count errors for display purposes
        const errorKeys = Object.keys(validationResults.errors)
        let errorCount = errorKeys.filter(k => k !== "items").length
        if (validationResults.errors["items"]) {
          errorCount += Object.keys(validationResults.errors["items"]).length
        }

        scrollToTopMostError(errorKeys, validationResults)

        console.error("validationResults", validationResults)

        return {
          status: "error",
          errorCount,
          message: (validationResults.errors as any).requiresOneItem
            ? "Must have at least 1 item"
            : undefined,
        }
      }

      setErrors({})

      let legacyPreview

      if (validationResults.warnings && validationResults.warnings.length > 0) {
        const confirmed = await getConfirmation({ warnings: validationResults.warnings })

        if (typeof confirmed === "object") {
          legacyPreview = confirmed.preview
        }

        if (!confirmed) {
          return { status: "cancel" }
        }
      }

      dataToSave.items = dataToSave.items.map(item => {
        const options = registeredItems[item.id]
        if (isDeleted(item) || !options || !options.onSave) {
          return item
        }
        const newItem = options.onSave(item, dataToSave)
        const returnItem = newItem || item
        delete returnItem.standard_discount_percent
        return returnItem
      })

      if (tapDataBeforeMutate) {
        dataToSave = await tapDataBeforeMutate(dataToSave)
      }

      const initialItemsById = filterDeleted(initialRecord?.items ?? []).reduce<
        Record<string, IItem>
      >((acc, item) => {
        acc[item.id] = item
        return acc
      }, {})
      const candidateItems = filterDeleted(dataToSave.items).filter(item => {
        if (isTempId(item.id)) {
          return true
        }
        const initialItem = initialItemsById[item.id]
        if (!initialItem) {
          return false
        }
        return (item.markUp ?? null) !== (initialItem.markUp ?? null)
      })
      const resetBypassMinMarginApproved = shouldResetBypassMinMarginApproved({
        record,
        candidateItems,
        user: currentUser,
      })

      const mutation = await convertDataToMutation(dataToSave, {
        resetBypassMinMarginApproved: resetBypassMinMarginApproved,
      })

      // Check to ensure the quote / order still has items and return error now if not (earlier than
      // this would allow the quote to be saved with blank items, which would be removed and bypass
      // this check)
      const dataObject = type === "order" ? mutation.orders : mutation.quotes
      const mutationKey = "id" in dataToSave ? "update" : "create"

      const items = dataObject[mutationKey]!.items
      const allItemsRemoved = items.every(mutation => "destroy" in mutation)
      if (items.length === 0 || allItemsRemoved) {
        return {
          status: "error",
          errorCount: 1,
          message: "You must include at least 1 item",
        }
      }

      if (type === "order") {
        if (dataToSave.will_collect && !dataToSave.billing_address) {
          return {
            status: "error",
            errorCount: 1,
            message: "Please select a billing address",
          }
        }
      }

      const exportedProducts: Record<string, string> = {}
      for (const item of items) {
        const itemData = item.create || item.update
        if (itemData) {
          const importerId = itemData.product?.create?.importer_id
          if (importerId) {
            if (!(importerId in exportedProducts)) {
              const product = await skeem.mutate<{}>({ products: itemData.product })
              exportedProducts[importerId] = product.id
            }
            itemData.product = { set: exportedProducts[importerId] }
          }
        }
      }

      try {
        await syncOrderRequestPrices(
          dataToSave.items,
          dataToSave.orderRequest?.id || initialRecord.orderRequest?.id,
        )
      } catch (e) {
        console.error(e)
        // if the OR or PO is missing, will use the custom error. otherwise return the actual error (likely an error
        // saving from skeem)
        let errorResponse = e.message
        if (e.message === "or_not_found") {
          errorResponse = `Cannot save order as the requesting branch Order Request no longer exists.`
        } else if (e.message === "po_not_found") {
          errorResponse = `Cannot save order as the requesting branch Purchase Order no longer exists. Contact support or archive the Order Request if this is expected.`
        }
        return {
          status: "error",
          errorCount: 1,
          message: errorResponse,
        }
      }
      let savedRecord: any
      try {
        savedRecord = await skeem.mutate<any>(mutation)
      } catch (e) {
        if (mutationKey === "create") {
          await skeem.mutate({
            alerts: {
              create: {
                type: "identifier-incremented-by-error",
                body: `An error occurred creating an ${
                  type || "quote/order"
                } which resulted in the branch identifier being incremented`,
                details: {
                  source: "QuotesOrdersForm",
                  raw: mutation,
                  error: e.message,
                  stack: e.stack,
                  branch: currentUser?.branch?.id ?? "",
                  user: currentUser?.id ?? "",
                },
                notifications: ["web@cd2solutions.co.uk"],
              },
            },
          })
        }
        throw e
      }

      // clear stock cache for all selected productLocationIds
      const productLocationIds = new Set(
        dataToSave.items
          .filter((item): item is IItem => !("$deleted" in item) || !item.$deleted)
          .map(item => item.product_location?.id)
          .filter(Boolean),
      )
      console.log("CLEARING CACHE FOR ALL THESE THINGS", productLocationIds)
      await api.productLocationsStockClear({ productLocationIds: Array.from(productLocationIds) })

      await postSaveCallback?.(savedRecord.projects?.[0]?.id)

      if (type === "quote" && mutationKey === "create") {
        await sendAlertLargeQuoteGenerated(savedRecord.id, total.totalPricePennies / 100)
      }

      sendLowAndHighMarginAlerts(skeem, savedRecord.id, type === "order" ? "orders" : "quotes")

      const isCd2 = currentUser.email.includes("@cd2solutions.co.uk")
      if (mutationKey === "update" && !isCd2) {
        const changes = buildTrackedChanges(initialRecord as any, record as any, currency)
        await events.updateRecord(type === "order" ? "orders" : "quotes", savedRecord.id, changes)
      }
      if (mutationKey === "create" && savedRecord.customer) {
        await events.createRecord(type === "order" ? "orders" : "quotes", savedRecord.id, {
          ...dataObject[mutationKey],
          customerPriceData: summariseCustomerPrices((window as any).customerPrices),
        })
        events.createAssociatedRecord("customers", savedRecord.customer.id, {
          association: type === "order" ? "orders" : "quotes",
          recordId: savedRecord.id,
        })
      } else if (savedRecord.customer) {
        const customerChanged =
          initialRecord?.customer && initialRecord?.customer.id !== savedRecord.customer.id
        if (customerChanged) {
          events.updateAssociatedRecord("customers", initialRecord.customer!.id, {
            association: type === "order" ? "orders" : "quotes",
            recordId: savedRecord.id,
            changes: {
              customer: { from: initialRecord.customer!.name, to: record.customer?.name },
            },
          })
        }
        events.updateAssociatedRecord("customers", savedRecord.customer.id, {
          association: type === "order" ? "orders" : "quotes",
          recordId: savedRecord.id,
          changes: customerChanged
            ? { customer: { from: initialRecord.customer?.name, to: record.customer?.name } }
            : undefined,
        })
      }

      if (mutationKey === "create" && type === "order") {
        if (job?.id) {
          await assignCustomerJobToOrder({
            orderId: savedRecord.id,
            jobId: job.id,
          })
          try {
            await api.emailCustomerOrderGenerated(savedRecord.id)
          } catch (e) {
            console.error("Error sending generated email")
          }
        }
        if (orderRequest?.id) {
          await assignOrderToOrderRequestAndNotify({
            orderId: savedRecord.id,
            orderRequestId: orderRequest.id,
          })
          await events.associateWithInverse("orders", savedRecord.id, {
            association: "orderRequests",
            recordId: orderRequest.id,
            nameAttr: "identifier",
          })
        }
      }

      if (mutationKey === "create" && type === "quote") {
        if (job?.id) {
          await assignCustomerJobToQuote({
            quoteId: savedRecord.id,
            jobId: job.id,
          })
        }
      }

      if (savedRecord.id && type == "order") {
        await runCreditLimitCheck(
          { skeem, events },
          {
            customerId: dataToSave.customer?.id ?? "",
            recordId: savedRecord.id,
            orderId: savedRecord.id,
          },
        )
      }

      return { status: "success", legacyPreview, savedRecord }
    } catch (e) {
      console.error(e)
      return {
        status: "error",
        errorCount: 1,
        message: "An error has occurred saving the form, please try again",
      }
    }
  }

  function sendLargeQuoteAlert({
    body,
    emails,
    details,
  }: {
    body: string
    emails: string[]
    details?: any
  }) {
    return skeem.mutate({
      alerts: {
        create: {
          body: body,
          details: details,
          type: "large_quote_alert",
          notifications: emails,
        },
      },
    })
  }

  async function sendAlertLargeQuoteGenerated(quoteId: string, value: number) {
    // branch directors: 10000+ – just for their branch
    // Ian Bennett and Steven Coulter get alerts for 20000+ – ideally for the branches they manage
    // Amanda gets alerts for 50000+ Quotes – All UK but not Ireland
    // + Dont trigger an email on an update - only a create

    if (!quoteId) {
      console.warn("Notify large quote: No id supplied")
      return
    }
    const quote = await skeem.fetch2<
      IRecord<"quotes", "identifier"> & {
        user?: IRecord<"skeem_users", "name">
        customer?: IRecord<"customers", "name">
        branch?: IRecord<"branches", "name"> & { settings: IBranchSettings }
      }
    >({
      first: {
        name: "quotes",
        attributes: [
          "identifier",
          { name: "user", attributes: ["name"] },
          { name: "customer", attributes: ["name"] },
          { name: "branch", attributes: ["name", "settings"] },
        ],
        matches: { id: quoteId },
      },
    })
    if (!quote) {
      console.warn("Notify large quote: Quote not found")
      return
    }

    const validBranches = await skeem.fetch2<IRecord<"branches">[]>({
      model: {
        name: "branches",
        filter: {
          and: [
            { not: { matches: { name: { like: "test" } } } },
            { not: { matches: { name: { like: "demo" } } } },
            { not: { matches: { name: { like: "training" } } } },
          ],
        },
      },
    })

    if (!validBranches.find(b => b.id === quote.branch?.id)) {
      console.warn("Not a valid branch for large quote notification")
      return
    }

    const isIrelandBranch = !!quote.branch?.name?.toLowerCase().match(/(Dublin|Cork)/gi)
    if (value >= 50000 && !isIrelandBranch) {
      console.log("notify - large quote 50k")
      // check notify amanda if not ireland branch
      await sendLargeQuoteAlert({
        emails: ["Amanda.Rowley@fortusuk.com"],
        body: `${quote.user?.name ?? "Unknown user"} has created a quote (${
          quote.identifier
        }) for ${quote.customer?.name ?? "Unknown customer"} on branch ${
          quote.branch?.name ?? "Unknown branch"
        } with a value greater than 50k.`,
      })
    }

    if (value >= 20000 && !isIrelandBranch) {
      // check notify ian/steven for quote branch
      const ianId = "0f56d67f-f20d-4ad4-9708-9bfba6ceba61" // live user id
      const stevenId = "1df5ac1e-0e78-40c4-8be8-61b07829c53b" // live user id
      const notifiableUsersIds = [ianId, stevenId]

      const userBranches = await skeem.fetch2<
        IRecord<"skeem_users", "skeem_email" | "branch" | "accessibleBranches">[]
      >({
        model: {
          name: "skeem_users",
          attributes: ["skeem_email", "branch", "accessibleBranches"],
          filter: { matches: { id: { or: notifiableUsersIds } } },
        },
      })

      const notifyUsers = userBranches.filter(user =>
        user?.accessibleBranches.find(branch => branch.id === quote.branch?.id),
      )

      if (notifyUsers.length > 0 && !isIrelandBranch) {
        console.log("notify - large quote 20k")
        await sendLargeQuoteAlert({
          emails: notifyUsers.map(u => u.skeem_email),
          body: `${quote.user?.name ?? "Unknown user"} has created a quote (${
            quote.identifier
          }) for ${quote.customer?.name ?? "Unknown customer"} on branch ${
            quote.branch?.name ?? "Unknown branch"
          } with a value greater than 20k.`,
        })
      }
    }

    if (value >= 10000 && !isIrelandBranch) {
      // check notify directors for quote branch
      const branchDirectorEmails = (
        quote.branch?.settings?.branchDirectorUsers.map(v => v.skeem_email) ?? []
      ).filter(Boolean)
      if (branchDirectorEmails.length > 0) {
        console.log("notify - large quote 10k")

        await sendLargeQuoteAlert({
          emails: branchDirectorEmails,
          body: `${quote.user?.name ?? "Unknown user"} has created a quote (${
            quote.identifier
          }) for ${quote.customer?.name ?? "Unknown customer"} on branch ${
            quote.branch?.name ?? "Unknown branch"
          } with a value greater than 10k.`,
        })
      }
    }
  }

  async function assignCustomerJobToOrder({ jobId, orderId }: { jobId: string; orderId: string }) {
    try {
      const job = await skeem.fetch2<IRecord<"jobs", "identifier" | "customer" | "reference">>({
        first: {
          name: "jobs",
          attributes: ["identifier", "customer", "reference"],
          filter: {
            matches: { id: jobId },
          },
        },
      })
      await skeem.mutate({
        jobs: {
          update: {
            id: job.id,
            status: jobStatusTypes.confirmed,
            order: {
              set: orderId,
            },
            childOrders: { add: { id: orderId, skipIfPresent: true } },
          },
        },
      })
      if (jobComments.isLoaded) {
        await jobComments.handleCommentSubmit(`An order has been assign to job ${job.reference}`)
      }
    } catch (e) {
      console.error("error associating customer job to order", e)
    }
  }

  async function assignCustomerJobToQuote({ jobId, quoteId }: { jobId: string; quoteId: string }) {
    try {
      const job = await skeem.fetch2<IRecord<"jobs", "identifier" | "customer" | "reference">>({
        first: {
          name: "jobs",
          attributes: ["identifier", "customer", "reference"],
          filter: {
            matches: { id: jobId },
          },
        },
      })
      await skeem.mutate({
        jobs: {
          update: {
            id: job.id,
            status: jobStatusTypes.confirmed,
            quote: {
              set: quoteId,
            },
          },
        },
      })

      if (jobComments.isLoaded) {
        await jobComments.handleCommentSubmit(`A quote has been assigned to job ${job.reference}`)
      }
    } catch (e) {
      console.error("error associating customer job to quote", e)
    }
  }

  async function syncOrderRequestPrices(
    dataItems: (IItem | IDeletedItem)[],
    orderRequestId?: string,
  ): Promise<boolean> {
    const items = dataItems.filter(item => !("$deleted" in item) && item.product?.id) as (IItem & {
      source_branch_product?: IRecord<"products", "body" | "cost_price_pennies" | "stock_code"> & {
        product_locations?: IRecord<
          "product_locations",
          "name" | "warehouse" | "free_stock" | "specific_location"
        >[]
      }
    })[]
    if (!orderRequestId) {
      return true
    }
    console.group("OR price sync")
    const orderRequest = await skeem.fetch2<
      IRecord<"orderRequests", "useDirectDelivery"> & {
        created_by_branch: IRecord<"branches", "name" | "consignmentWarehouse"> & {
          settings: IBranchSettings
        }
        purchase_order: IRecord<"purchase_orders"> & {
          purchase_order_items: IRecord<"purchase_order_items", "product" | "price_pennies">[]
        }
        items: IRecord<"orderRequestItems", "source_product" | "target_product" | "price_pennies">[]
      }
    >({
      first: {
        name: "orderRequests",
        attributes: [
          "useDirectDelivery",
          {
            name: "created_by_branch",
            attributes: ["name", "consignmentWarehouse", "settings"],
          },
          {
            name: "purchase_order",
            attributes: [
              {
                name: "purchase_order_items",
                attributes: ["product"],
                filter: { exists: { attr: "product" } },
              },
            ],
          },
          {
            name: "items",
            attributes: ["source_product", "target_product", "price_pennies"],
            filter: {
              and: [{ exists: { attr: "source_product" } }, { exists: { attr: "target_product" } }],
            },
          },
        ],
        matches: { id: orderRequestId },
      },
    })
    // Early return will guarantee we have OR + PO.
    if (!orderRequest?.purchase_order) {
      console.log("Missing OR or PO", {
        or: orderRequest?.id,
        po: orderRequest?.purchase_order?.id,
      })
      console.groupEnd()
      throw new Error(!orderRequest ? "or_not_found" : "po_not_found")
    }

    // lookup products from the OR created_by_branch and set source_branch_product to allow updating
    // PO product if product changes
    const productIds = items.map(item => item.product?.id).filter(Boolean)
    const productData = await skeem.fetch2<
      (IRecord<"products"> & {
        product_master?: IRecord<"product_masters"> & {
          products: IRecord<"products", "body" | "cost_price_pennies" | "stock_code">[]
        }
      })[]
    >({
      model: {
        name: "products",
        attributes: [
          {
            name: "product_master",
            attributes: [
              {
                name: "products",
                attributes: ["stock_code", "body", "cost_price_pennies"],
                filter: { matches: { branch: orderRequest.created_by_branch?.id, active: true } },
              },
            ],
          },
        ],
        matches: { id: { or: productIds } },
      },
    })
    items.forEach(item => {
      const product = productData.find(p => p.id === item.product?.id)
      if (product) {
        // try and match on stock code before assuming first, to avoid issues where products
        // are assigned to the wrong product master due to similar stock codes (e.g abc/rl and abcrl)
        const matchingStockCodeProduct = product.product_master?.products.find(
          x => x.stock_code.toLowerCase() === item.stock_code.toLowerCase(),
        )
        item.source_branch_product =
          matchingStockCodeProduct || product.product_master?.products?.[0]
        console.log(
          "matched product",
          item.stock_code,
          matchingStockCodeProduct,
          product.product_master?.products?.[0],
        )
      }
    })

    const sourceBranchProductIds = [
      ...new Set(
        items.map(item => item.source_branch_product?.id).filter((id): id is string => !!id),
      ),
    ]
    const sourceProductsById: Record<
      string,
      IRecord<"products"> & {
        product_locations: IRecord<
          "product_locations",
          "name" | "warehouse" | "free_stock" | "specific_location"
        >[]
      }
    > = {}
    if (sourceBranchProductIds.length > 0) {
      const sourceProducts = await skeem.fetch2<
        (IRecord<"products"> & {
          product_locations: IRecord<
            "product_locations",
            "name" | "warehouse" | "free_stock" | "specific_location"
          >[]
        })[]
      >({
        model: {
          name: "products",
          attributes: [
            {
              name: "product_locations",
              attributes: ["name", "warehouse", "free_stock", "specific_location"],
            },
          ],
          matches: { id: { or: sourceBranchProductIds } },
        },
      })
      for (const product of sourceProducts) {
        sourceProductsById[product.id] = product
      }
    }

    const itemData: {
      orderItem: (typeof items)[number]
      orItem: (typeof orderRequest)["items"][number]
      poItem: (typeof orderRequest)["purchase_order"]["purchase_order_items"][number]
    }[] = []

    // Matching the order request items to the order items.
    for (const orderItem of items) {
      const orItem = orderRequest.items.find(
        orItem =>
          orItem.target_product?.id === orderItem.originalProductId &&
          !itemData.find(item => item.orItem.id === orItem.id),
      )
      if (!orItem) {
        console.warn("Failed to match Order request item for product", orderItem.product?.id)
        continue
      }
      const poItem = orderRequest.purchase_order.purchase_order_items.find(
        poItem =>
          poItem.product?.id === orItem?.source_product?.id &&
          !itemData.find(item => item.poItem.id === poItem.id),
      )
      if (!poItem) {
        console.warn(
          "Failed to match Purchase order item for product",
          orderItem.product?.id,
          orItem,
        )
        continue
      }
      itemData.push({ orderItem, orItem, poItem })
    }
    console.log("Matched", itemData.length, "of", items.length, "items")

    const orItemMutations: {
      update: {
        id: string
        stock_code?: string
        body?: string
        price_pennies: number | null
        source_product?: { set: string }
        source_cost_price_pennies?: number | null
        target_cost_price_pennies: number | null
        target_product?: { set: string }
      }
    }[] = []
    const poItemMutations: {
      update: {
        id: string
        price_pennies: number | null
        product?: { set: string }
        stock_code?: string
        product_body?: string
        product_location?: { set: string | null }
        body?: string
      }
    }[] = []
    const priceSyncs: {
      name: string
      current: string
      previous: string
    }[] = []
    itemData.forEach(item => {
      const sourceProductChange =
        item.orderItem.source_branch_product &&
        item.orItem.source_product?.id !== item.orderItem.source_branch_product?.id
      const orProductChange = item.orderItem.product!.id !== item.orItem.target_product?.id

      orItemMutations.push({
        update: {
          id: item.orItem.id,
          price_pennies: item.orderItem.price_pennies,

          // Only update product details if the item actually changed
          stock_code: sourceProductChange
            ? item.orderItem.source_branch_product!.stock_code
            : undefined,
          body: sourceProductChange ? item.orderItem.source_branch_product!.body : undefined,
          source_cost_price_pennies: sourceProductChange
            ? item.orderItem.source_branch_product!.cost_price_pennies
            : undefined,
          source_product: sourceProductChange
            ? { set: item.orderItem.source_branch_product!.id }
            : undefined,

          target_cost_price_pennies: item.orderItem.cost_price_pennies,
          // Only update target product if the target product actually changed
          target_product: orProductChange ? { set: item.orderItem.product!.id } : undefined,
        },
      })
      const priceA = asPrice(item.orderItem.price_pennies, currency)
      const priceB = asPrice(item.orItem.price_pennies, currency)
      if (priceA !== priceB) {
        priceSyncs.push({
          name: item.orderItem.stock_code,
          current: priceA,
          previous: priceB,
        })
      }

      const poProductChange =
        item.orderItem.source_branch_product &&
        item.poItem.product?.id !== item.orderItem.source_branch_product?.id

      const sourceBranch = orderRequest.created_by_branch
      const sourceBranchSettings = sourceBranch.settings
      const poProductLocation = poProductChange
        ? getProductLocationPrioritized(
            sourceProductsById[item.orderItem.source_branch_product!.id]?.product_locations ?? [],
            {
              directDelivery: orderRequest.useDirectDelivery ?? false,
              directDeliveryWarehouse: sourceBranchSettings?.directDeliveryWarehouse,
              defaultStockLocation: sourceBranchSettings?.defaultStockLocation ?? "",
              branch: sourceBranch,
              defaultIntercoWarehouse: sourceBranchSettings?.defaultIntercoWarehouse,
            },
            { isInterco: true },
          )
        : undefined

      poItemMutations.push({
        update: {
          id: item.poItem.id,
          price_pennies: item.orderItem.price_pennies,
          product: poProductChange ? { set: item.orderItem.source_branch_product!.id } : undefined,
          stock_code: poProductChange
            ? item.orderItem.source_branch_product!.stock_code
            : undefined,
          product_body: poProductChange ? item.orderItem.source_branch_product!.body : undefined,
          product_location: poProductChange ? { set: poProductLocation?.id ?? null } : undefined,
        },
      })
    })

    try {
      await skeem.mutate({ orderRequestItems: orItemMutations })
      console.log("Saved OR items", orItemMutations.length, orItemMutations)
      if (priceSyncs.length > 0) {
        events.updateRecord("orderRequests", orderRequest.id, [
          {
            name: "Price Sync",
            type: "update",
            data: priceSyncs.map(item => ({
              name: item.name,
              type: "update",
              current: item.current,
              previous: item.previous,
            })),
          },
        ])
      }
    } catch (e) {
      console.error(e)
      alerts.genericError("Failed to sync prices with order request")
      throw new Error(e.message)
    }
    try {
      await skeem.mutate({ purchase_order_items: poItemMutations })
      console.log("Saved PO items", poItemMutations.length, poItemMutations)
      if (priceSyncs.length > 0) {
        events.updateRecord("purchase_orders", orderRequest.purchase_order.id, [
          {
            name: "Price Sync",
            type: "update",
            data: priceSyncs.map(item => ({
              name: item.name,
              type: "update",
              current: item.current,
              previous: item.previous,
            })),
          },
        ])
      }
    } catch (e) {
      console.error(e)
      alerts.genericError("Failed to sync prices with purchase order")
      throw new Error(e.message)
    }
    console.log("Sync end")
    console.groupEnd()
    return true
  }

  async function assignOrderToOrderRequestAndNotify({
    orderRequestId,
    orderId,
  }: {
    orderRequestId: string
    orderId: string
  }) {
    try {
      const orderRequest = await skeem.fetch2<
        IRecord<"orderRequests", "identifier"> & {
          created_by_branch: IRecord<"branches", "name">
          received_by_branch: IRecord<"branches", "name">
          purchase_order?: IRecord<"purchase_orders", "identifier">
        }
      >({
        first: {
          name: "orderRequests",
          attributes: [
            "identifier",
            {
              name: "created_by_branch",
              attributes: ["name"],
            },
            {
              name: "received_by_branch",
              attributes: ["name"],
            },
            {
              name: "purchase_order",
              attributes: ["identifier"],
            },
          ],
          filter: {
            and: [{ matches: { id: orderRequestId } }],
          },
        },
      })

      await skeem.mutate({
        orderRequests: {
          update: {
            id: orderRequest.id,
            status: orderRequestStatusTypes.confirmed,
            order: {
              set: orderId,
            },
            childOrders: {
              add: orderId,
            },
          },
        },
      })

      await sendOrderCreatedForOrderRequestNotification(orderRequest.id)
    } catch (e) {
      console.error("failed associating order request and sending notification", e)
    }
  }

  useEffect(() => {
    async function fn() {
      if (
        type === "order" &&
        previousPurchaseOrderReferenceRef.current !== record.purchase_order_reference
      ) {
        shouldValidatePurchaseOrderReferenceRef.current = true
        hasADuplicatePurchaseOrderReferenceRef.current = false
      }

      const validationResults = await validateForm(record)
      setErrors(validationResults.errors)
    }

    fn()
  }, [record])

  useEffect(() => {
    if (willCollect) {
      setCustomBillingAddress(true)
    }
  }, [willCollect])

  async function convertDataToMutation(
    data: IQuoteOrderRecord,
    opts?: { resetBypassMinMarginApproved?: boolean },
  ) {
    if (!currentUser.branch.id) {
      alerts.genericError(
        "You are no longer signed in and cannot save this record. To prevent losing the data, please sign in in a new tab and attempt to save again.",
      )
      throw new Error("Unauthenticated")
    }

    let nextItemWeight = 1
    // Separate the deleted items and actual items
    const itemsDeleted = data.items.filter(item => "$deleted" in item)
    const changedItems = filterDeleted(data.items)
    // Get our parent items as the actual items for ordering
    const parentItems = changedItems.filter(item => !itemIsChild(item))
    const dataItems: (IItem | IDeletedItem)[] = []
    // Map over our parent items, find their children and push them in directly after the parent
    for (const item of parentItems) {
      dataItems.push(item)
      const children = changedItems.filter(child => itemIsChildOf(child, item))
      if (children.length) {
        dataItems.push(...children)
      }
    }
    // Re-add the deleted items for the mutation at the end of the array
    dataItems.push(...itemsDeleted)

    const nonDeletedItems = filterDeleted(data.items)
    const locationIds = nonDeletedItems.map(item => item.product_location?.id).filter(Boolean)
    let stockData: Record<string, IProductLocationStockResponseItem> = {}
    if (locationIds.length > 0) {
      stockData = await api.productLocationsStock(locationIds)
    }

    const filteredItems = dataItems
      .filter(
        (item, i) =>
          !isDeleted(item) || dataItems.findIndex(innerItem => innerItem.id === item.id) === i,
      )
      .filter(item => {
        // we don't need to process the ones which are deleted and not saved in the database
        if (isTempId(item.id) && isDeleted(item)) {
          return false
        } else {
          return true
        }
      })

    // filtering out deleted items that are pushed into the mutation multiple times
    const itemMutations = filteredItems
      .map(item => {
        if (
          isDeleted(item) ||
          !item.product_location?.id ||
          !item.product?.id ||
          !settings.useExplicitAllocations ||
          type === "quote"
        ) {
          return item
        }

        if (item.type === itemTypes.buildSet) {
          // do the stock checks for the child items first
          // then work out a % of actually available to determine how many of parent can be allocated
          const childItems = filteredItems.filter(
            childItem =>
              !isDeleted(childItem) &&
              childItem.build_set_uid === item.build_set_uid &&
              childItem.type === itemTypes.buildSetItem,
          ) as IItem[]
          // re-assign allocated quantity based on child item availability
          for (const cItem of childItems) {
            if (!cItem.product_location?.id) {
              continue
            }
            const inStock = stockData[cItem.product_location.id].inStock
            const orderStock = sum(
              stockData[cItem.product_location?.id].allocatedOrders?.filter(
                o => o.id === data.id,
              ) ?? [],
              x => x?.[settings.useExplicitAllocations ? "allocatedQuantity" : "quantity"] ?? 0,
            )
            const allocatedStock =
              stockData[cItem.product_location?.id]?.[
                settings.useExplicitAllocations ? "explicitlyAllocatedStock" : "allocatedStock"
              ]
            const qty = cItem.quantity ?? 1
            const allocatable = Math.max(
              0,
              Math.min(cItem.allocated_quantity ?? 0, inStock + orderStock - allocatedStock),
            )
            cItem.allocated_quantity = allocatable
            // if we cannot allocate the full quantity for this sub item, work out the maximum number of builds that
            // can be made for this particular item
            if (allocatable < qty) {
              const buildable = Math.floor(allocatable / (cItem.build_quantity ?? 1))
              // if the max buildable for this item is less that our current allocation for the parent, reduce the
              // parents allocated so it only allocates actually supplied items
              if (buildable < (item.allocated_quantity ?? 1)) {
                item.allocated_quantity = buildable
              }
            }
          }
          // check the parent items availability based
          const inStock = stockData[item.product_location.id].inStock
          const orderStock =
            stockData[item.product_location?.id].allocatedOrders?.find(o => o.id === data.id)?.[
              settings.useExplicitAllocations ? "allocatedQuantity" : "quantity"
            ] ?? 0
          const allocatedStock =
            stockData[item.product_location?.id]?.[
              settings.useExplicitAllocations ? "explicitlyAllocatedStock" : "allocatedStock"
            ]
          item.allocated_quantity = Math.min(
            item.allocated_quantity ?? 0,
            inStock + orderStock - allocatedStock,
          )

          if ((item.allocated_quantity ?? 0) < (item.quantity ?? 0)) {
            // loop over the child items and ensure their allocated quantities match the buildable quantity
            // so we dont have an item that as 1/1 on a child, but 0/1 on parent
            childItems.forEach(subItem => {
              subItem.allocated_quantity =
                (subItem.build_quantity ?? 0) * (item.allocated_quantity ?? 0)
            })
          }
          return {
            ...item,
            allocated_quantity: item.allocated_quantity,
          }
        } else if (item.type !== itemTypes.buildSetItem) {
          // only do the stock checks for non build set items as the build set items stock was
          // handled in the above condition
          const inStock = stockData[item.product_location?.id].inStock
          const orderStock =
            stockData[item.product_location?.id].allocatedOrders?.find(o => o.id === data.id)?.[
              settings.useExplicitAllocations ? "allocatedQuantity" : "quantity"
            ] ?? 0
          const allocatedStock =
            stockData[item.product_location?.id]?.[
              settings.useExplicitAllocations ? "explicitlyAllocatedStock" : "allocatedStock"
            ]

          return {
            ...item,
            allocated_quantity: Math.max(
              0,
              Math.min(item.allocated_quantity ?? 0, inStock + orderStock - allocatedStock),
            ),
          }
        }

        return item
      })
      .map(item => {
        if (isDeleted(item)) {
          return { destroy: item.id }
        } else if (isTempId(item.id)) {
          return {
            create: {
              ...item,
              id: undefined,
              price_pennies: item.price_pennies
                ? Math.round(item.price_pennies)
                : item.price_pennies,
              parentId: undefined,
              weight: nextItemWeight++,
              product: { set: item.product?.id ?? null },
              product_location: { set: item.product_location?.id ?? null },
              is_rich_quote_optional: type !== "quote" ? undefined : item.is_rich_quote_optional,
              sage_show_on_customer_documents:
                type == "order" ? item.sage_show_on_customer_documents : undefined,
              // Stripping data that is not a real attribute by setting to undefined
              originalProductId: undefined,
              price_locked: undefined,
              build_quantity: undefined,
              oppCertDisabled: undefined,
            },
          }
        } else {
          // When updating, set the product and locations to null if not of the right type
          // In cases where for some reason the product is not available despite being a sage
          // product, just skip the update
          let product
          let product_location

          if (item.type !== itemTypes.custom && item.type !== itemTypes.text) {
            if (!item.product?.id) {
              product = undefined
            } else {
              product = { set: item.product.id }
            }

            if (!item.product_location?.id) {
              product_location = undefined
            } else {
              product_location = { set: item.product_location.id }
            }
          } else {
            product = { set: null }
            product_location = { set: null }
          }

          return {
            update: {
              ...item,
              price_pennies: item.price_pennies
                ? Math.round(item.price_pennies)
                : item.price_pennies,
              parentId: undefined,
              weight: nextItemWeight++,
              product,
              product_location,
              is_rich_quote_optional: type !== "quote" ? undefined : item.is_rich_quote_optional,
              sage_show_on_customer_documents:
                type == "order" ? item.sage_show_on_customer_documents : undefined,
              // Stripping data that is not a real attribute by setting to undefined
              originalProductId: undefined,
              price_locked: undefined,
              build_quantity: undefined,
              oppCertDisabled: undefined,
            },
          }
        }
      })

    type ICustomerAddress = IRecord<
      "addresses",
      | "name"
      | "line_1"
      | "line_2"
      | "line_3"
      | "city"
      | "county"
      | "country"
      | "postcode"
      | "phone"
      | "email"
    >
    let customerAddresses: ICustomerAddress[] = []
    if (data.customer && disableBillingAddress && type === "order") {
      customerAddresses = await skeem.fetch2<ICustomerAddress[]>({
        model: {
          name: "addresses",
          attributes: [
            "name",
            "line_1",
            "line_2",
            "line_3",
            "city",
            "county",
            "country",
            "postcode",
            "phone",
            "email",
          ],
          filter: {
            and: [
              { matches: { customer: data.customer.id, isDefault: true } },
              { not: { matches: { importer_id: "" } } },
              { not: { matches: { line_1: "" } } },
            ],
          },
        },
      })
    }

    // These are split out as (A) they should never be updated and (B) to prevent a potential issue
    // Where they could become unset if updating a record on the wrong branch or during some period
    // of being unauthenticated.
    const isNewRecord = !data.id
    const newRecordAttrs = isNewRecord
      ? {
          branch: { set: currentUser.branch.id },
          source_branch_id: type === "order" ? currentUser.branch.id ?? undefined : undefined,
          user: { set: currentUser.id },
        }
      : {}

    let attrMutation: {
      branch?: { set: string | null }
      source_branch_id?: string
      user?: { set: string | null }
      identifier: string
      carriage_pennies: number | null
      items: Record<string, any>[]
      customer?: { set: string | null }
      sub_customer?: { set: string | null }
      contact?: { set: string | null }
      opportunityCertificateFile: {
        set?: string | null
        update?: any
        create?: any
        destroy?: string | null
      }
      internal_transfer_to_branch?: { set: string | null }
      address?: Record<string, any>
      billing_address?: Record<string, any>
      amended?: boolean
      will_collect?: boolean
      notes?: string
      required_date?: Date
      projects?: any
      files?: any[]
      currency_name: string
      bypassMinMarginApproved?: boolean
    } = {
      ...newRecordAttrs,
      currency_name: data.currency_name ?? "",
      projects: data.projects,
      identifier: data.identifier,
      carriage_pennies: data.carriage_pennies,
      will_collect: data.will_collect,
      items: itemMutations,
      customer:
        type === "order"
          ? data.customer
            ? { set: data.customer.id }
            : undefined
          : { set: data.customer?.id ?? null },
      sub_customer: { set: data.sub_customer?.id ?? null },
      contact: data.contact ? { set: data.contact.id } : undefined,
      internal_transfer_to_branch: {
        set:
          data.internal_transfer_to_branch && data.isInternalTransfer
            ? data.internal_transfer_to_branch.id
            : null,
      },
      notes: data.notes,
      required_date: data.required_date,
      files: data.files
        ?.map(file => {
          if (file._destroy && !file.id) {
            return null
          }
          if (file.id && file._destroy) {
            return { destroy: file.id }
          }
          if (file.id) {
            return { update: file }
          } else {
            return { create: file }
          }
        })
        .filter(Boolean),
      opportunityCertificateFile: doo(() => {
        if (!record.opportunityCertificateFile) {
          return { set: null }
        }
        const id = record.opportunityCertificateFile.id
        if (id?.startsWith("temp")) {
          // if new record
          if (record.opportunityCertificateFile._destroy) {
            // never saved + destroy
            return { set: null }
          }
          // never saved - create
          return {
            create: {
              fileboyId: record.opportunityCertificateFile.fileboyId,
              fileName: "Opportunity Certificate",
            },
          }
        } else {
          // if existing record
          if (record.opportunityCertificateFile._destroy) {
            // existing - destroy
            return { destroy: id }
          }
          // existing - update
          return {
            update: {
              id: id,
              fileboyId: record.opportunityCertificateFile.fileboyId,
            },
          }
        }
      }),
      address: data.will_collect
        ? { set: null }
        : data.address.id && checkAddressHasData(data.address)
        ? { update: data.address }
        : data.address.id && !checkAddressHasData(data.address)
        ? { set: null }
        : { create: { ...data.address, branch: { set: currentUser.branch.id } } },
      billing_address: doo(() => {
        if (type == "order") {
          // No billing address selector and we don't have an ID, create a new one from the default
          if (disableBillingAddress && !data.billing_address?.id && customerAddresses[0]) {
            return {
              create: {
                ...customerAddresses[0],
                id: undefined,
                importer_id: "",
                branch: { set: currentUser.branch.id },
              },
            }
          } else if (data.billing_address && customBillingAddress) {
            const isAddressWithData = checkAddressHasData(data.billing_address)
            // If we have an associated address but its missing core data, unassociated it
            // so that we infer to use shipping address details
            if (data.billing_address.id && !isAddressWithData) {
              return { set: null }
            }

            // if the address has an ID attached in the data, use that to update
            if (data.billing_address.id && isAddressWithData) {
              return { update: data.billing_address }
            }

            // if we have billing address values with no associated ID, create it as new address
            return { create: { ...data.billing_address, branch: { set: currentUser.branch.id } } }
          }
          return { set: null }
        }
        return undefined
      }),
    }

    if (tapMutationBuilding) {
      attrMutation = tapMutationBuilding(attrMutation, data)
    }

    if (type === "order" && data.id && !!data.pdf_generated) {
      attrMutation.amended = true
    }
    if (type === "order" && data.id && opts?.resetBypassMinMarginApproved) {
      attrMutation.bypassMinMarginApproved = false
    }

    const mutation = {
      [type === "quote" ? "quotes" : "orders"]:
        "id" in data ? { update: { id: data.id, ...attrMutation } } : { create: attrMutation },
    }

    return mutation
  }

  const [registeredItems, setRegisteredItems] = useState<Record<string, IQuoteItemRegisterOptions>>(
    {},
  )

  function registerItem(item: IItem, options: IQuoteItemRegisterOptions) {
    setRegisteredItems(registeredItems => ({ ...registeredItems, [item.id]: options }))

    return () => {
      setRegisteredItems(registeredItems => {
        const newRegisteredItems = { ...registeredItems }
        delete newRegisteredItems[item.id]
        return newRegisteredItems
      })
    }
  }

  function handleCustomerPanelChange(
    recordPartial: IComponentCallback<typeof CustomerPanel, "onChange">,
  ) {
    setRecord(fullRecord => {
      const differentCustomer =
        recordPartial.customer && recordPartial.customer?.id !== fullRecord.customer?.id

      const hasShippingData = !!recordPartial.shippingAddress
      const shippingAddress = recordPartial.shippingAddress || recordPartial.address

      const addressToSet = differentCustomer
        ? recordPartial.customer
          ? {
              ...shippingAddress,
              id: record.address?.id,
              importer_id: undefined,
            }
          : {
              id: record.address?.id,
              importer_id: undefined,
            }
        : fullRecord.address

      const billingAddress = differentCustomer
        ? customBillingAddress || (type === "order" && hasShippingData)
          ? { ...recordPartial.address, id: record.billing_address?.id }
          : { id: record.billing_address?.id }
        : fullRecord.billing_address

      if (
        type === "order" &&
        differentCustomer &&
        hasShippingData &&
        shippingAddress.id !== recordPartial.address?.id
      ) {
        setCustomBillingAddress(true)
      } else if (type === "order" && differentCustomer && !hasShippingData) {
        setCustomBillingAddress(startWithBillingAddressUnchecked)
      }
      delete recordPartial.shippingAddress

      const dpdProductDetails: {
        dpdProduct?: any
        delivery_method?: string
        carriage_pennies?: number | null
      } = {
        dpdProduct: recordPartial.dpdProduct ?? fullRecord.dpdProduct,
      }
      // The panel can turn this on itself when an internal customer is picked, so read the
      // incoming partial first rather than the record it is about to replace.
      const isInternalTransfer =
        (recordPartial as any).isInternalTransfer ?? (fullRecord as any).isInternalTransfer
      if (
        barScanOrders &&
        !record.warehouseOverride?.disable_barscan &&
        !isInternalTransfer &&
        recordPartial.customer?.defaultDeliveryService
      ) {
        dpdProductDetails.dpdProduct = recordPartial.customer?.defaultDeliveryService
        dpdProductDetails.delivery_method = dpdProductDetails.dpdProduct?.body
        dpdProductDetails.carriage_pennies = dpdProductDetails.dpdProduct?.sale_price_pennies
        recordPartial.carriage_pennies = dpdProductDetails.dpdProduct?.sale_price_pennies
      }
      if (
        (!barScanOrders || record.warehouseOverride?.disable_barscan) &&
        !isInternalTransfer &&
        recordPartial.customer?.defaultDeliveryMethod
      ) {
        dpdProductDetails.delivery_method = recordPartial.customer.defaultDeliveryMethod
      }

      const result = {
        ...fullRecord,
        ...recordPartial,
        ...dpdProductDetails,
        address: addressToSet,
        billing_address: billingAddress,
        carriage_pennies: recordPartial.carriage_pennies ?? fullRecord.carriage_pennies,
      }
      return result
    })
  }

  function handleAddressChange(
    addressPartial: Partial<IAddress>,
    opts?: { fullOverride?: boolean },
  ) {
    setRecord(record => {
      const addressData = opts?.fullOverride
        ? // Retain ID here, as this is the quote/orders address ID, and should be done as an update.
          { id: record.address?.id, ...addressPartial, importer_id: undefined }
        : { ...record.address, ...addressPartial, importer_id: undefined }
      return { ...record, address: addressData }
    })
  }

  function handleBillingAddressChange(
    addressPartial: Partial<IAddress> | undefined,
    opts?: { fullOverride?: boolean },
  ) {
    setRecord(record => {
      const addressData = opts?.fullOverride
        ? // Retain ID here, as this is the quote/orders address ID, and should be done as an update.
          { id: record.billing_address?.id, ...addressPartial, importer_id: undefined }
        : {
            ...record.billing_address,
            ...addressPartial,
            importer_id: undefined,
          }
      return { ...record, billing_address: addressPartial && addressData }
    })
  }

  function handleClearAddress() {
    setRecord(record => {
      const address = record.address
      for (const key in address) {
        if (key !== "id") {
          address[key as keyof typeof address] = ""
        }
      }
      return {
        ...record,
        address: { ...address },
      }
    })
  }

  function handleClearBillingAddress() {
    setRecord(record => {
      const address = record.billing_address ?? {}
      for (const key in address) {
        if (key !== "id") {
          address[key as keyof typeof address] = ""
        }
      }
      return {
        ...record,
        billing_address: { ...address },
      }
    })
  }

  /**
   * @param item IItem
   * @param opts optional arguments
   * @skipAdditionalChecks skip childItem validations for sage rows
   */
  function doesItemHaveErrors(item: IItem, opts?: { skipAdditionalChecks?: boolean }) {
    const registeredItem = registeredItems[item.id]

    // lets just check if the errors state has anything saved before running the validation functions again
    // this also will display red for async validations which currently only run on save
    if (errors.items && errors.items[item.id]) {
      errors.items[item.id]
      return true
    }

    // no validate method means no errors
    if (!registeredItem || !registeredItem.validate) {
      return false
    }

    const results = registeredItem.validate(item, record)

    if (!results) {
      return false
    }

    if (results.errors && Object.keys(results.errors).length > 0) {
      return true
    }

    if (results.warnings && results.warnings.length > 0) {
      return true
    }

    // Run validations on child items and return them as errors for the main item
    // where called, skipAdditionalChecks is set to true for childItems to prevent recursion.
    if (!opts?.skipAdditionalChecks && item.type === itemTypes.sage) {
      const childItems = getProductItemChildren(record, item)
      if (childItems.length > 0) {
        for (const child of childItems) {
          if (doesItemHaveErrors(child, { skipAdditionalChecks: true })) {
            return true
          }
        }
      }
    }

    return false
  }

  const billingAddressBody = (
    <React.Fragment>
      <Spacer size={8} />
      <Fields.Checkbox
        label="Same as delivery address"
        value={!customBillingAddress}
        onChange={value => setCustomBillingAddress(!value)}
        disabled={willCollect}
      />
    </React.Fragment>
  )

  async function handleWillCollectChange(state: boolean) {
    if (!record.customer) {
      return
    }
    const attrs = [
      "label",
      "postcode",
      "contact",
      "name",
      "line_1",
      "line_2",
      "line_3",
      "line_4",
      "city",
      "county",
      "country",
      "email",
      "phone",
      "importer_id",
    ] as const
    if (state && !record.billing_address) {
      const result = await skeem.fetch2<{
        defaultAddresses: IRecord<"addresses", (typeof attrs)[number]>[]
        addresses: IRecord<"addresses", (typeof attrs)[number]>[]
      }>({
        first: {
          name: "customers",
          attributes: [
            {
              as: "defaultAddresses",
              name: "addresses",
              attributes: attrs,
              filter: {
                and: [
                  { matches: { isDefault: true } },
                  { matches: { deleted: false } },
                  { matches: { showInCustomerAddressOptions: true } },
                ],
              },
            },
            {
              name: "addresses",
              attributes: attrs,
              filter: {
                and: [
                  { matches: { deleted: false } },
                  { matches: { showInCustomerAddressOptions: true } },
                ],
              },
            },
          ],
          filter: { matches: { id: record.customer.id } },
        },
      })
      if (result.defaultAddresses.length > 0) {
        const { id: _, ...address } = result.defaultAddresses[0]
        handleBillingAddressChange(address)
      } else if (result.addresses.length > 0) {
        const { id: _, ...address } = result.addresses[0]
        handleBillingAddressChange(address)
      }
    }
    setWillCollect(state)
  }

  const deliveryAddressBody = (
    <React.Fragment>
      <Spacer size={8} />
      <Fields.Checkbox
        label="Customer will collect from branch"
        value={willCollect}
        onChange={value => handleWillCollectChange(value)}
      />
    </React.Fragment>
  )

  const onHold = record.customer && !!record.customer?.account_on_hold
  const canCreateOnHold = type === "order" ? settings.enableOnHoldOrders : true

  let creditLimitStatus: "" | "at" | "over" = ""
  if (record.customer?.outstanding_balance_pennies) {
    const limit = record.customer.credit_limit_pennies ?? 0
    if (record.customer.outstanding_balance_pennies > limit) {
      creditLimitStatus = "over"
    } else if (record.customer.outstanding_balance_pennies === limit) {
      creditLimitStatus = "at"
    }
  }

  function wouldBeOverLimit() {
    if (!record.customer) {
      return false
    }
    if (!record.customer?.credit_limit_pennies) {
      return false
    }
    if (record.customer.credit_limit_pennies === 0) {
      return false
    }
    const itemsNotDeleted = record.items.filter(item => !("$deleted" in item)) as IItem[]
    const { totalPricePennies: total } = getTotals(
      itemsNotDeleted,
      record.carriage_pennies,
      record.customer?.tax_rate,
    )
    const forecastOutstanding = (record.customer.outstanding_balance_pennies || 0) + total
    return forecastOutstanding > record.customer?.credit_limit_pennies
  }

  const [updatingCostPrices, setUpdatingCostPrices] = useState(false)
  const [refetchDifferentCostPricesKey, setRefetchDifferentCostPricesKey] = useState(1)

  const [itemsWithDifferentCostPrices, setItemsWithDifferentCostPrices] = useState<
    {
      id: string
      oldPrice: number
      newPrice: number
      product: IRecord<"products", "cost_price_pennies" | "body" | "stock_code">
    }[]
  >([])

  useAsyncEffect(async () => {
    await checkItemsWithDifferentCostPrices()
    // const timeout = setTimeout(() => {
    //   setRefetchDifferentCostPricesKey(k => k + 1)
    // }, 4000)
    //
    // return () => {
    //   clearTimeout(timeout)
    // }
  }, [refetchDifferentCostPricesKey])

  async function checkItemsWithDifferentCostPrices() {
    const recordItems = record.items as IItem[]
    const recordProductItems = recordItems.filter(
      item => !!item.product && ![itemTypes.buildSet].includes(item.type ?? 0),
    )

    // return early if no sage product items
    if (recordProductItems.length === 0) {
      return
    }

    const products = await skeem.fetch<
      IRecord<"products", "cost_price_pennies" | "body" | "stock_code"> & {
        suppliers: IRecord<"suppliers", "currency_name">[]
        preferred_supplier?: IRecord<"suppliers", "currency_name">
        product_group: IRecord<"product_groups", "products_price_method">
        branch: IRecord<"branches", "currency_name">
      }
    >({
      products: {
        attributes: [
          "cost_price_pennies",
          "body",
          "stock_code",
          { name: "suppliers", attributes: ["currency_name"] },
          { name: "preferred_supplier", attributes: ["currency_name"] },
          { name: "product_group", attributes: ["products_price_method"] },
          { name: "branch", attributes: ["currency_name"] },
        ],
        filter: { or: recordProductItems.map(item => ({ matches: { id: item.product!.id } })) },
      },
    })
    const groupPricedItems = await Promise.all(
      products.map(product => getGroupCostPrice(product.id)),
    )

    const differentItems: typeof itemsWithDifferentCostPrices = []

    recordProductItems.forEach(item => {
      const groupPrice = groupPricedItems.find(
        gp => item.product && gp?.original_product_id === item.product.id,
      )
      const product = products.find(p => p.id === item.product?.id)
      const costPrice = product
        ? calculateCostPriceFromCurrency({
            currentUser,
            product,
            currency_name: record.currency_name,
            groupPrice,
          })
        : null
      if (!product) {
        return
      }

      if (
        !(costPrice
          ? fuzzyNumberCompare(costPrice, item.cost_price_pennies, 1)
          : fuzzyNumberCompare(product.cost_price_pennies, item.cost_price_pennies, 1)) &&
        ![itemTypes.buildSet, itemTypes.buildSetItem].includes(item.type ?? 0)
      ) {
        differentItems.push({
          id: item.id,
          product: { ...product, cost_price_pennies: costPrice ?? product.cost_price_pennies },
          oldPrice: item.cost_price_pennies || 0,
          newPrice: costPrice ?? (product.cost_price_pennies || 0),
        })
      }
    })

    setItemsWithDifferentCostPrices(differentItems)
  }

  const [confirmProductPriceUpdateNode, confirmProductPriceUpdate] = useConfirmCostPriceUpdate(
    record.customer?.id,
  )

  const handleUpdateCostPrices = useAsyncCallback(async () => {
    setUpdatingCostPrices(true)
    const recordItems = record.items as IItem[]
    const recordProductItems = recordItems.filter(
      item => !!item.product && ![itemTypes.buildSet].includes(item.type ?? 0),
    )
    const newItemData = await confirmProductPriceUpdate({
      currencyName: record.currency_name || currentUser.branch.currency_name,
      items: recordProductItems,
    })
    if (newItemData) {
      const items = newItemData.items
      if (Array.isArray(items)) {
        const itemData = recordItems.map(v => items.find(item => item.id === v.id) || v)
        setRecord({ ...record, items: itemData })
        alerts.genericSuccess("Prices updated")
      }
    }
    setUpdatingCostPrices(false)
    setRefetchDifferentCostPricesKey(k => k + 1)
  })

  const disableBillingAddress = doo(() => {
    if (
      settings.showBillingAddressOnOrders ||
      (record.customer?.cash_sales && settings.alwaysShowBillingForCashSalesOnOrders)
    ) {
      return false
    }
    return (record.customer?.addresses ?? []).length === 1
  })

  const alertBanners = [
    record.customer?.cash_sales && {
      type: "error",
      message: "The selected customer account is cash sales only",
    },
    onHold &&
      ({
        type: canCreateOnHold ? "warning" : "error",
        message: "The selected customer account is on hold",
      } as const),
    !!creditLimitStatus &&
      ({
        message: `The selected customer account is ${creditLimitStatus} their credit limit`,
        type: "warning",
      } as const),
    wouldBeOverLimit() &&
      !creditLimitStatus &&
      ({
        message: `This ${type} will take the customer over their credit limit of ${formatPrice(
          record.customer?.credit_limit_pennies ?? 0,
          {
            isPennies: true,
            currency,
          },
        )}`,
        type: "warning",
      } as const),
    directDeliverySourceOrder && {
      message: `This is a direct delivery order to a customer from the ${directDeliverySourceOrder.branch?.name} branch`,
      type: "info" as const,
    },
  ].filter(Boolean)

  return (
    <React.Fragment>
      <Toolbar
        items={[
          {
            children: "Customer Viewing",
            onClick: () => setCustomerViewing(!isCustomerViewing),
            buttonColor: isCustomerViewing ? brandColors.darkMint : brandColors.lightNight,
            icon: isCustomerViewing ? icons.toggleOn : icons.toggleOff,
            small: true,
            color: "white",
          },
        ]}
      />
      <PageWrapper
        title={title}
        breadcrumbPath={[type === "quote" ? "quotes" : "orders", { title }]}
        minWidth={950}
        pageActions={() => (
          <ButtonsWrapper>
            {!isSmall && (
              <SaveButtons onClick={handleSaveButtonsClick} isSubmitting={isSubmitting} />
            )}

            <QuickToolTip
              content={
                <div>
                  <TextBlock color="white" size="small">
                    <P>
                      Click this button to update the prices with the latest prices from Sage. You
                      will be able to select how to update the prices.
                    </P>
                    {itemsWithDifferentCostPrices.length === 0 && (
                      <P>
                        <b>There are no items on this quote with an updated cost price</b>
                      </P>
                    )}
                  </TextBlock>
                </div>
              }
            >
              <div>
                <Button
                  leftIcon={icons.refresh}
                  onClick={handleUpdateCostPrices}
                  disabled={updatingCostPrices || itemsWithDifferentCostPrices.length === 0}
                >
                  Update prices...
                </Button>
              </div>
            </QuickToolTip>
          </ButtonsWrapper>
        )}
      >
        <PreventPageNavigation
          when={dirty}
          message="You have unsaved changes, are you sure you wish to leave?"
        />
        <div>
          {projectNode}
          {confirmProductPriceUpdateNode}
          {confirmationNode}
          {alertBelowMinimumMargin}
          {directDeliveryLocationCheckNode}
          {useCacheModal}
          <DebugPanel data={{ quote: record, errors }} />
          <AlertBanners banners={alertBanners as any} />
          {alertBanners.length > 0 && <Spacer size={16} />}

          <Grid
            columns={{ repeat: { times: "auto-fit", size: { min: 250, max: { fr: 1 } } } }}
            alignItems="end"
          >
            <CustomerPanel
              quote={record}
              onChange={handleCustomerPanelChange}
              errors={errors}
              CustomerHeadingFields={CustomerHeadingFields}
              onChangeDpdProduct={data => setRecord(old => ({ ...old, ...data }))}
              isQuote={type === "quote"}
              isNewPage={isNewPage}
              disableCustomerSelect={disableCustomerSelect}
            />

            <React.Fragment>
              <Spacer />
              <FloatingBox
                color="#f5f5f5"
                css={css`
                  z-index: 2;
                  position: relative;
                  background-color: #f5f5f5;
                `}
              >
                {type === "order" && (
                  <React.Fragment>
                    <div>
                      <Checkbox
                        label="Auto send order acknowledgement"
                        onChange={value => {
                          if (settings.allowDisableAutoOrderRequestEmail) {
                            if (!value) {
                              setRecord(old => ({
                                ...old,
                                autoEmailOrderAcknowledgementOn: undefined,
                              }))
                            } else {
                              setRecord(old => ({
                                ...old,
                                autoEmailOrderAcknowledgementOn:
                                  !barScanOrders || record.warehouseOverride?.disable_barscan
                                    ? "onDelivery"
                                    : "onExport",
                              }))
                            }
                          }
                        }}
                        value={
                          !settings.allowDisableAutoOrderRequestEmail ||
                          !!record.autoEmailOrderAcknowledgementOn
                        }
                        description="This will email the order acknowledgement automatically at the chosen point."
                        disabled={!settings.allowDisableAutoOrderRequestEmail}
                      />
                      {(!settings.allowDisableAutoOrderRequestEmail ||
                        !!record.autoEmailOrderAcknowledgementOn) && (
                        <div
                          css={css`
                            border: 1px solid
                              ${errors.autoEmailOrderAcknowledgementOn
                                ? "red"
                                : brandColors.midGrey};
                            padding: 4px;
                          `}
                        >
                          <FieldWithDescription
                            description={`Order Acknowledgement will be sent to: ${
                              record.contact?.email || record.delivery_note_email || "-"
                            }`}
                          >
                            <AutoSendOrderAcknowledgementCheckbox
                              onChange={value =>
                                setRecord(old => ({
                                  ...old,
                                  autoEmailOrderAcknowledgementOn: value,
                                }))
                              }
                              disable_barscan={!!record.warehouseOverride?.disable_barscan}
                              value={(record as any).autoEmailOrderAcknowledgementOn}
                              disabled={!(record.contact?.email || record.delivery_note_email)}
                            />
                          </FieldWithDescription>
                        </div>
                      )}
                      {errors.autoEmailOrderAcknowledgementOn && (
                        <P color="red" size="small">
                          {errors.autoEmailOrderAcknowledgementOn}
                        </P>
                      )}
                    </div>
                    <Spacer />
                  </React.Fragment>
                )}
                {!settings.useQuoteOrderStandardPricing &&
                  settings.canUseOpportunityCertificate && (
                    <React.Fragment>
                      <Fields.Checkbox
                        label="Has Opportunity Certificate"
                        onChange={value => {
                          if (process.env.NODE_ENV === "development" && value) {
                            setRecord(old => ({
                              ...old,
                              hasOpportunityCertificate: value,
                              opportunityCertificateFile: {
                                id: "temp-0.35228466599415353",
                                fileName: "Opportunity Certificate",
                                fileboyId: "3581359a-22ee-48e3-aef9-3149285ee6b6",
                                _destroy: false,
                              },
                            }))
                          }
                          setRecord(old => ({ ...old, hasOpportunityCertificate: value }))
                        }}
                        value={!!record.hasOpportunityCertificate}
                      />
                      <Spacer />
                      <div>
                        {record.hasOpportunityCertificate && (
                          <FileUploader
                            key={`${record.opportunityCertificateFile?._destroy}-${record.opportunityCertificateFile?.id}`}
                            onFileUploaded={fileData => {
                              setRecord(record => ({
                                ...record,
                                opportunityCertificateFile: {
                                  id: `temp-${Math.random()}`,
                                  fileName: "Opportunity Certificate",
                                  fileboyId: fileData.id,
                                  _destroy: false,
                                },
                              }))
                            }}
                            defaultFileId={
                              record.opportunityCertificateFile?._destroy
                                ? undefined
                                : record.opportunityCertificateFile?.fileboyId
                            }
                            uploaderStyle="minimal"
                          />
                        )}

                        {record.opportunityCertificateFile?.fileboyId ? (
                          <Button
                            onClick={() => {
                              setRecord(record => ({
                                ...record,
                                opportunityCertificateFile: {
                                  _destroy: true,
                                  id: record.opportunityCertificateFile?.id,
                                } as any,
                              }))
                            }}
                            buttonStyle="text"
                          >
                            Remove
                          </Button>
                        ) : null}

                        {errors?.opportunityCertificateFile ? (
                          <P size="small" color="red">
                            {errors?.opportunityCertificateFile}
                          </P>
                        ) : null}
                      </div>
                      <Spacer />
                    </React.Fragment>
                  )}
                <Fields.Text
                  label="Project Registration Number"
                  value={record.projectRegistrationNumber || ""}
                  onChange={value =>
                    handleCustomerPanelChange({ projectRegistrationNumber: value })
                  }
                  error={errors.projectRegistrationNumber}
                  showRequiredStar={record.hasOpportunityCertificate}
                />
                <Spacer />
                <Fields.Text
                  label="Opportunity Name"
                  onChange={value => setRecord(old => ({ ...old, opportunity_name: value }))}
                  value={record.opportunity_name}
                />
                <Spacer />

                {type === "order" && (
                  <React.Fragment>
                    <CommercialInvoiceInput
                      quote={record}
                      onChange={handleCustomerPanelChange}
                      errors={errors}
                    />
                    <Spacer />
                  </React.Fragment>
                )}

                <FieldWithDescription description="These notes will not appear on customer documents">
                  <Fields.TextArea
                    value={record.notes ?? ""}
                    onChange={value => setRecord(record => ({ ...record, notes: value }))}
                    label="Notes"
                    rows="auto"
                    placeholder="Enter any notes you wish to add..."
                  />
                </FieldWithDescription>
              </FloatingBox>
            </React.Fragment>
          </Grid>
          <Spacer />
          {record.items.some(item => !isDeleted(item) && item.is_rich_quote_optional) && (
            <React.Fragment>
              <Fields.Checkbox
                label="Include optional items on customer documents"
                onChange={() => {
                  setRecord(record => ({
                    ...record,
                    showOptionalItemsInCustomerDocuments:
                      !record.showOptionalItemsInCustomerDocuments,
                  }))
                }}
                value={!!record.showOptionalItemsInCustomerDocuments}
              />
              <Spacer />
            </React.Fragment>
          )}
          <ItemTable
            type={type}
            quote={record}
            setQuote={setRecord}
            setQuoteSilent={_setRecord}
            errors={errors}
            registerItem={registerItem}
            registeredItems={registeredItems}
            doesItemHaveErrors={doesItemHaveErrors}
            isNew={isNewPage}
            isCustomerViewing={isCustomerViewing}
            hideMarginColumn={shipToCustomerOrder}
            hidePriceColumn={shipToCustomerOrder}
          />
          <Spacer size={14} />
          <div style={{ textAlign: "right" }}>
            <SaveButtons onClick={handleSaveButtonsClick} isSubmitting={isSubmitting} />
          </div>

          <Spacer size={14} />

          {record?.customer?.id ? (
            <React.Fragment>
              {willCollect ? (
                <FloatingBox>
                  <div css={css(`max-width: 500px`)}>
                    <TextGroupMedium heading="Delivery Address" body={deliveryAddressBody} />
                  </div>
                </FloatingBox>
              ) : (
                <AddressesSectionAsForm
                  heading="Delivery Address"
                  body={deliveryAddressBody}
                  selectedAddress={record.address as any}
                  onAddressSelect={({ id: _id, ...address }) => handleAddressChange(address)}
                  onAddressOverride={({ id: _id, ...address }) =>
                    handleAddressChange(address, { fullOverride: true })
                  }
                  onClearAddress={handleClearAddress}
                  onAfterMutateAddress={refetchAddresses}
                  customerId={record?.customer?.id}
                  xRef={deliveryAddressRef}
                  deliveryService={doo(() => {
                    if (!getShouldValidateAddressLength()) {
                      return undefined
                    }
                    if (!settings.barScanOrders) {
                      return record.delivery_method
                    }
                    if (settings.barScanOrders) {
                      return record.dpdProduct?.body
                    }
                    return undefined
                  })}
                  errors={errors?.address}
                />
              )}

              {type === "order" && !disableBillingAddress && (
                <React.Fragment>
                  <Spacer size={14} />
                  {customBillingAddress ? (
                    <AddressesSectionAsForm
                      heading="Billing Address"
                      body={billingAddressBody}
                      selectedAddress={{ ...(record.billing_address || {}) } as any}
                      onAddressSelect={({ id: _id, ...address }) =>
                        handleBillingAddressChange(address)
                      }
                      onAddressOverride={({ id: _id, ...address }) =>
                        handleBillingAddressChange(address, { fullOverride: true })
                      }
                      onClearAddress={handleClearBillingAddress}
                      onAfterMutateAddress={refetchAddresses}
                      customerId={record?.customer?.id}
                      xRef={billingAddressRef}
                      deliveryService={doo(() => {
                        if (!settings.barScanOrders) {
                          return record.delivery_method
                        }
                        if (settings.barScanOrders) {
                          return record.dpdProduct?.body
                        }
                        return undefined
                      })}
                      errors={errors?.billing_address}
                    />
                  ) : (
                    <FloatingBox>
                      <div css={css(`max-width: 500px`)}>
                        <TextGroupMedium heading="Billing Address" body={billingAddressBody} />
                      </div>
                    </FloatingBox>
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          ) : (
            <FloatingBox>
              <TextGroupMedium
                heading="Address"
                body="Select a customer above to view their addresses."
              />
            </FloatingBox>
          )}
          <Spacer size={14} />
          <AttachmentsField
            onAddFile={newFile => {
              setRecord(record => ({ ...record, files: [...(record.files ?? []), newFile] }))
            }}
            onRemove={removeFileboyIds => {
              setRecord(record => ({
                ...record,
                files: record.files?.map(file =>
                  removeFileboyIds.includes(file.fileboyId) ? { ...file, _destroy: true } : file,
                ),
              }))
            }}
            onUpdate={updatedFile => {
              setRecord(record => ({
                ...record,
                files: record.files.map(f =>
                  f.fileboyId === updatedFile.fileboyId ? { ...f, notes: updatedFile.notes } : f,
                ),
              }))
            }}
            error={errors.files}
            value={((record.files as any[]) ?? []).filter(
              (f: { _destroy?: boolean }) => !f._destroy,
            )}
          />
          <Spacer size={14} />
          <ButtonsWrapper
            css={css`
              justify-content: flex-end;
            `}
          >
            <SaveButtons onClick={handleSaveButtonsClick} isSubmitting={isSubmitting} />
          </ButtonsWrapper>
          <Spacer size={14} />
        </div>
      </PageWrapper>
    </React.Fragment>
  )
}

export async function sendLowAndHighMarginAlerts(
  skeem: IWebClient,
  recordId: string,
  type: "quotes" | "orders",
) {
  const record = await skeem.fetch2<
    IRecord<
      "orders" | "quotes",
      "identifier" | "internal_transfer_to_branch"
    > & {
      items: (IRecord<
        "order_items" | "quote_items",
        "price_pennies" | "cost_price_pennies" | "stock_code"
      > & {
        minMargin: number | null
        maxMargin: number | null
        product_group: string | null
      })[]
      branchSettings: IBranchSettings
      branch: IRecord<"branches", "name">
      user: IRecord<"skeem_users", "name">
    }
  >({
    first: {
      name: type,
      attributes: [
        "identifier",
        "internal_transfer_to_branch",
        { name: "branch", attributes: ["name"] },
        { name: "user", attributes: ["name"] },
        {
          name: "items",
          attributes: [
            "price_pennies",
            "cost_price_pennies",
            "stock_code",
            { as: "product_group", value: { path: ["product", "product_group", "code"] } },
            { as: "minMargin", value: { path: ["product", "product_group", "minimum_margin"] } },
            { as: "maxMargin", value: { path: ["product", "product_group", "maximum_margin"] } },
          ],
        },
        { as: "branchSettings", value: { path: ["branch", "settings"] } },
      ],
      filter: { matches: { id: recordId } },
    },
  })

  // Do not send alerts for internal transfers
  if (record.internal_transfer_to_branch?.id) {
    return
  }

  const typeSingle = type.slice(0, -1)

  const orderItems = record.items
  const emails = [
    ...record.branchSettings.notificationEmailsProductGroupMargins,
    // "support@cd2solutions.co.uk",
  ]

  if (emails.length === 0) {
    return
  }

  const itemsWithMargins = orderItems.map(item => ({
    ...item,
    margin: calculateMarginFromPrice(item.cost_price_pennies ?? 0, item.price_pennies ?? 0),
  }))
  const itemsExceedingMaxMargin = itemsWithMargins.filter(
    item => item.maxMargin && item.margin > item.maxMargin,
  )
  const itemsBelowMinMargin = itemsWithMargins.filter(
    item => item.minMargin && item.margin < item.minMargin,
  )

  if (itemsExceedingMaxMargin.length > 0) {
    const alertBody = `
  <p>${type === "quotes" ? "Quote" : "Order"} <b>${record.identifier}</b> for branch ${
      record.branch.name
    } has items with margins exceeding the maximum margin set on the product group. Set by ${
      record.user.name
    }.</p>
  <table>
  <tr>
    <th style="text-align: left; padding: 4px">Product Code</th>
    <th style="text-align: left; padding: 4px">Product Group</th>
    <th style="text-align: left; padding: 4px">Selling Price</th>
    <th style="text-align: left; padding: 4px">Margin</th>
  </tr>
  ${itemsExceedingMaxMargin
    .map(
      item => `
    <tr>
      <td style="text-align: left; padding: 4px">${item.stock_code}</td>
      <td style="text-align: left; padding: 4px">${item.product_group}</td>
      <td style="text-align: left; padding: 4px">${formatPrice(item.price_pennies ?? 0, {
        isPennies: true,
      })}</td>
      <td style="text-align: left; padding: 4px">${calculateMarginFromPrice(
        item.cost_price_pennies ?? 0,
        item.price_pennies ?? 0,
      )}</td>
    </tr>`,
    )
    .join("")}
  </table>
  `
    await skeem.mutate({
      alerts: {
        create: {
          type: `${typeSingle}-item-margin-exceeded`,
          body: alertBody,
          details: record,
          notifications: emails,
        },
      },
    })
  }
  if (itemsBelowMinMargin.length > 0) {
    const alertBody = `
  <p>${type === "quotes" ? "Quote" : "Order"} <b>${record.identifier}</b> for branch ${
      record.branch.name
    } has items with margins below the minimum margin set on the product group. Set by ${
      record.user.name
    }.</p>
  <table>
  <tr>
    <th style="text-align: left; padding: 4px">Product Code</th>
    <th style="text-align: left; padding: 4px">Product Group</th>
    <th style="text-align: left; padding: 4px">Selling Price</th>
    <th style="text-align: left; padding: 4px">Margin</th>
  </tr>
  ${itemsBelowMinMargin
    .map(
      item => `
    <tr>
      <td style="text-align: left; padding: 4px">${item.stock_code}</td>
      <td style="text-align: left; padding: 4px">${item.product_group}</td> 
      <td style="text-align: left; padding: 4px">${formatPrice(item.price_pennies ?? 0, {
        isPennies: true,
      })}</td>
      <td style="text-align: left; padding: 4px">${calculateMarginFromPrice(
        item.cost_price_pennies ?? 0,
        item.price_pennies ?? 0,
      )}</td>
    </tr>`,
    )
    .join("")}
  </table>
  `
    await skeem.mutate({
      alerts: {
        create: {
          type: `${typeSingle}-item-margin-exceeded`,
          body: alertBody,
          details: record,
          notifications: emails,
        },
      },
    })
  }
}

export type IAttachmentShowFile = IRecord<
  "files",
  "fileName" | "fileboyId" | "notes" | "created_at"
> & {
  _destroy?: boolean
}

export function AttachmentsField({
  onAddFile,
  onRemove,
  onUpdate,
  value,
  error,
}: {
  onAddFile(file: Omit<IAttachmentShowFile, "id">): void
  onRemove(files: string[]): void
  onUpdate?(file: Omit<IAttachmentShowFile, "id">): any
  value: Omit<IAttachmentShowFile, "id">[]
  error?: string
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const fileboy = useFileboy()

  const [editNotesNode, editNotes] = useConfirmation<Omit<IAttachmentShowFile, "id">, void>(
    ({ data, onResolve }) => (
      <Form2.Form
        onSubmit={async ({ notes }) => {
          await onUpdate?.({ ...data, notes: notes })
          onResolve()
        }}
        initialData={data}
      >
        <Form2.WithForm
          render={form => (
            <ConfirmCancelDialogue
              heading="Notes for this file"
              onConfirm="submit"
              onCancel={() => onResolve()}
              body={
                <React.Fragment>
                  <Spacer />
                  <Form2.TextArea field="notes" />
                </React.Fragment>
              }
              width={600}
              buttonOpts={{
                confirm: {
                  children: "Update",
                  onClick: event => {
                    // stops trigger nested form submits - manually fire this forms submit
                    event.stopPropagation()
                    event.preventDefault()
                    form.submit()
                  },
                },
              }}
              isOpen
            />
          )}
        />
      </Form2.Form>
    ),
  )

  async function handleEditNotes(file: Omit<IAttachmentShowFile, "id">) {
    await editNotes(file)
  }

  function handleFileCheckboxClick(file: (typeof value)[number]) {
    setSelectedIds(current =>
      current.includes(file.fileboyId)
        ? current.filter(id => id !== file.fileboyId)
        : [...current, file.fileboyId],
    )
  }

  return (
    <React.Fragment>
      {/* stops page reloading when submitted if rendered in nested forms */}
      {document.getElementById("app-scene-portal-ref") &&
        createPortal(editNotesNode, document.getElementById("app-scene-portal-ref")!)}

      <Grid
        columns={{ repeat: { times: "auto-fit", size: { min: 300, max: { fr: 1 } } } }}
        gap={16}
      >
        <div
          css={css`
            width: 100%;
            max-width: 100%;
            overflow: auto;
          `}
        >
          {value.length > 0 && (
            <div>
              <Grid columns={{ auto: { min: 100, max: 120 } }} gap={4}>
                {value
                  .sort((a, b) => {
                    if (a.created_at && b.created_at) {
                      return new Date(a.created_at).valueOf() - new Date(b.created_at).valueOf()
                    }
                    return a.fileboyId.localeCompare(b.fileboyId)
                  })
                  .map(file => (
                    <div
                      key={file.fileboyId}
                      css={css`
                        border: 1px solid ${brandColors.midGrey};
                        border-radius: 6px;
                        padding: 4px;
                      `}
                    >
                      <QuickToolTip content={file.fileName}>
                        <div
                          css={css`
                            position: relative;
                            cursor: pointer;
                            border-radius: 6px;
                            overflow: hidden;
                          `}
                        >
                          <div
                            css={css`
                              position: absolute;
                              top: 2px;
                              left: 2px;
                              z-index: 2;
                            `}
                            onClick={event => {
                              event.stopPropagation()
                              handleFileCheckboxClick(file)
                            }}
                          >
                            <Fields.Checkbox
                              onChange={() => {}}
                              value={selectedIds.includes(file.fileboyId)}
                            />
                          </div>

                          <div
                            css={css`
                              svg {
                                height: 64px;
                              }
                            `}
                          >
                            <FilePreview id={file.fileboyId} />
                          </div>
                        </div>
                      </QuickToolTip>

                      {selectedIds.length === 0 && (
                        <Grid gap={4}>
                          <a
                            href={fileboy.buildFileUrl(file.fileboyId, {
                              download: true,
                              filename: file.fileName,
                            })}
                            onClick={event => {
                              if (selectedIds.length > 0) {
                                event.preventDefault()
                                event.stopPropagation()
                                handleFileCheckboxClick(file)
                              }
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button
                              extendTheme={{ padding: { sides: 8, topAndBottom: 4 }, fontSize: 14 }}
                              leftIcon={icons.download}
                              block
                            >
                              Download
                            </Button>
                          </a>
                          {onUpdate && (
                            <Button
                              buttonStyle={file.notes ? "solid" : "outline"}
                              onClick={() => handleEditNotes(file)}
                              extendTheme={{ padding: { sides: 8, topAndBottom: 4 }, fontSize: 14 }}
                              leftIcon={icons.memo}
                              block
                            >
                              {file.notes && file.notes.length > 2
                                ? file.notes.slice(0, 7) + ".."
                                : "Notes"}
                            </Button>
                          )}
                        </Grid>
                      )}
                    </div>
                  ))}
              </Grid>
            </div>
          )}
          {selectedIds.length > 0 && (
            <div
              css={css`
                padding: 2px;
              `}
            >
              <BaseButton
                {...buttonConfigs.smallInlineSaveButton}
                renderLeft={() => <Icon icon={icons.delete} />}
                onClick={() => {
                  onRemove(selectedIds)
                  setSelectedIds([])
                }}
              >
                Remove selected
              </BaseButton>
            </div>
          )}
        </div>
        <div>
          <FileUploader
            onFileUploaded={file => {
              onAddFile({
                fileboyId: file.id,
                fileName: file.originalFilename,
                notes: "",
                created_at: new Date().toJSON(),
              })
            }}
            render={({ DropZone, isUploading, openFileDialogue }) => (
              <DropZone>
                <div
                  css={css`
                    border: 1px dashed ${error ? themeColors.alertRed : colors.lightBorder};
                    border-radius: 6px;
                    padding: 16px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    cursor: pointer;
                    background-color: #f4f4f4;
                  `}
                  onClick={openFileDialogue}
                >
                  {isUploading ? (
                    <Spinner color="black" />
                  ) : (
                    <div>
                      <P size="large" align="center">
                        <Icon icon={icons.upload} /> Upload documents and images
                      </P>
                      <Spacer size={4} />
                      <P align="center" size="small">
                        drag and drop files to upload
                      </P>
                    </div>
                  )}
                </div>
              </DropZone>
            )}
          />
          {error && (
            <P color="error" blockSpacing={0} size="small">
              {error}
            </P>
          )}
        </div>
      </Grid>
    </React.Fragment>
  )
}

export function FilesListPreview({
  files,
}: {
  files: Omit<IRecord<"files", "fileName" | "fileboyId">, "id">[]
}) {
  const fileboy = useFileboy()
  return (
    <FloatingBox header={<H4>Attachments</H4>}>
      {files.length === 0 && <NoResults noResultsNode={<P>Documents added will appear here.</P>} />}
      {files.length > 0 && (
        <Grid columns={{ auto: { min: 40, max: 80 } }}>
          {files.map(file => (
            <QuickToolTip key={file.fileboyId} content={file.fileName}>
              <div
                css={css`
                  position: relative;
                  cursor: pointer;

                  .show-download {
                    opacity: 0;
                  }

                  :hover {
                    .show-download {
                      opacity: 1;
                    }
                  }

                  border-radius: 6px;
                  overflow: hidden;
                `}
              >
                <a
                  href={fileboy.buildFileUrl(file.fileboyId, {
                    download: true,
                    filename: file.fileName,
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div
                    className="show-download"
                    css={css`
                      position: absolute;
                      top: 0;
                      left: 0;
                      width: 100%;
                      height: 100%;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      background-color: rgba(0, 0, 0, 0.3);
                    `}
                  >
                    <Icon icon={icons.download} size={24} />
                  </div>
                  <div
                    css={css`
                      svg {
                        height: 64px;
                      }
                    `}
                  >
                    <FilePreview id={file.fileboyId} />
                  </div>
                </a>
              </div>
            </QuickToolTip>
          ))}
        </Grid>
      )}
    </FloatingBox>
  )
}

interface IDDWarehouseCheckProps {
  directDeliveryWarehouse?: { id: string }
  items: { product_location?: IRecord<"product_locations", "warehouse"> }[]
}

function useDirectDeliveryLocationsCheck() {
  const [confirmNode, handleConfirm] = useConfirmation<void, boolean>(({ onResolve }) => (
    <ConfirmCancelDialogue
      heading="Not all items are for the direct delivery warehouse"
      body={
        <React.Fragment>
          <P>
            The delivery method for this order was specified as direct delivery, however one or more
            items warehouses are not set to the direct delivery warehouse.
          </P>
          <P>Are you sure you want to proceed?</P>
        </React.Fragment>
      }
      onCancel={() => onResolve(false)}
      onConfirm={() => onResolve(true)}
      isOpen
    />
  ))

  async function handleWarnDirectDeliveryLocation(data: IDDWarehouseCheckProps) {
    if (!data.directDeliveryWarehouse) {
      return true
    }
    if (
      data.items.some(
        item =>
          item.product_location &&
          item.product_location.warehouse?.id !== data.directDeliveryWarehouse?.id,
      )
    ) {
      return handleConfirm()
    }
    return true
  }

  return { directDeliveryLocationCheckNode: confirmNode, handleWarnDirectDeliveryLocation }
}

interface IChangeItem {
  id?: string
  stock_code?: string
  type?: number
  quantity?: string
  allocated_quantity?: number | null
  product_location?: { id: string; name: string }
  cost_price_pennies?: number | null
  price_pennies?: number | null
  margin?: number | null
  body?: string
  $deleted?: boolean
}

interface IChangeTrackingData {
  customer?: { id: string; name: string }
  lead?: { id: string; name: string }
  contact?: { id: string; name: string }
  internal_transfer_to_branch?: { id: string; name: string }
  delivery_note_email?: string
  purchase_order_reference?: string
  delivery_method?: string
  dpd_delivery_info?: string
  items?: IChangeItem[]
  address?: Record<string, any>
  billing_address?: Record<string, any>
  carriage_pennies?: number | null
  projectRegistrationNumber?: string
  origin?: any
  weight?: any
  dimensions?: any
  pallet_size?: any
}

function asPrice(value?: number | null, currency: any) {
  if (!value) {
    return ""
  }
  return formatPrice(value, { currency, isPennies: true })
}

function buildTrackedChanges(
  previous: IChangeTrackingData,
  current: IChangeTrackingData,
  currency: "£" | "€" | "",
) {
  /**
   * customer
   * internal?
   * contact
   * delivery note email
   * po reference
   * delivery method
   * dpd delivery info
   * required date
   * project reg number
   * commerical invoice deets - origin, weight, dimensions, pallets
   * notes?
   * carraige
   * delivery address / will collect
   * billing address
   * documents add/remove/update
   * items: add / remove / update
   *  - product (stock code?)
   *  - quantity
   *  - location
   *  - unit cost
   *  - margin
   *  - text type: body
   */
  interface IChange {
    name: string
    type: "add" | "update" | "remove"
    display?: { type: "hoverLoader"; model: IEventModel }

    previous?: any
    current?: any
    data?: IChange[]
  }

  function diffItemAttrs(item1?: IChangeItem | null, item2?: IChangeItem | null) {
    return (
      [
        { attr: "stock_code", compare: (x, y) => x !== y },
        { attr: "type", compare: (x, y) => x !== y },
        { attr: "body", compare: (x, y) => x !== y },
        { attr: "price_pennies", compare: (x, y) => x !== y },
        { attr: "cost_price_pennies", compare: (x, y) => x !== y },
        { attr: "margin", compare: (x, y) => x !== y },
        { attr: "quantity", compare: (x, y) => x !== y },
        {
          attr: "allocated_quantity",
          compare: (x, y) => (x ?? 0) !== (y ?? 0),
        },
        { attr: "product_location", compare: (x, y) => x?.id !== y?.id },
      ] as {
        attr: keyof IChangeItem
        compare(x: any, y: any): boolean
      }[]
    ).filter(v => v.compare(item1?.[v.attr], item2?.[v.attr]))
  }

  function buildItemChange(previous?: IChangeItem | null, current?: IChangeItem | null) {
    const attrs = diffItemAttrs(previous, current)
    const itemChanges: IChange[] = []
    attrs.forEach(attr => {
      if (attr.attr === "stock_code") {
        itemChanges.push({
          name: "Stock Code",
          type: "update",
          previous: previous?.stock_code,
          current: current?.stock_code,
        })
      }
      if (attr.attr === "type") {
        const typeMap = ["Product", "Custom Product", "Text Line"]
        itemChanges.push({
          name: "Item Type",
          type: "update",
          previous: typeMap[previous?.type ?? -1] ?? "",
          current: typeMap[current?.type ?? -1] ?? "",
        })
      }
      if (attr.attr === "body") {
        itemChanges.push({
          name: "Body",
          type: "update",
          previous: previous?.body,
          current: current?.body,
        })
      }
      if (attr.attr === "cost_price_pennies") {
        itemChanges.push({
          name: "Unit Price",
          type: "update",
          previous: asPrice(previous?.cost_price_pennies, currency),
          current: asPrice(current?.cost_price_pennies, currency),
        })
      }

      if (attr.attr === "price_pennies") {
        itemChanges.push({
          name: "Price",
          type: "update",
          previous: asPrice(previous?.price_pennies, currency),
          current: asPrice(current?.price_pennies, currency),
        })
      }

      if (attr.attr === "margin") {
        itemChanges.push({
          name: "Margin",
          type: "update",
          previous: previous?.margin,
          current: current?.margin,
        })
      }

      if (attr.attr === "quantity") {
        itemChanges.push({
          name: "Quantity",
          type: "update",
          previous: previous?.quantity,
          current: current?.quantity,
        })
      }

      if (attr.attr === "allocated_quantity") {
        itemChanges.push({
          name: "Allocated Quantity",
          type: "update",
          previous: previous?.allocated_quantity ?? 0,
          current: current?.allocated_quantity ?? 0,
        })
      }

      if (attr.attr === "product_location") {
        itemChanges.push({
          name: "Location",
          type: "update",
          previous: previous?.product_location?.name,
          current: current?.product_location?.name,
        })
      }
    })
    return itemChanges
  }

  const changeData: IChange[] = []
  if (previous.customer?.id !== current.customer?.id) {
    changeData.push({
      name: "Customer",
      type: "update",
      display: { type: "hoverLoader", model: "customers" },
      previous: previous.customer?.id,
      current: current.customer?.id,
    })
  }
  if (previous.internal_transfer_to_branch?.id !== current.internal_transfer_to_branch?.id) {
    changeData.push({
      name: "Internal Transfer",
      type: "update",
      display: { type: "hoverLoader", model: "branches" },
      previous: previous.internal_transfer_to_branch?.id,
      current: current.internal_transfer_to_branch?.id,
    })
  }
  if (previous.lead?.id !== current.lead?.id) {
    changeData.push({
      name: "Lead",
      type: "update",
      display: { type: "hoverLoader", model: "leads" },
      previous: previous.lead?.id,
      current: current.lead?.id,
    })
  }

  if (previous.contact?.id !== current.contact?.id) {
    changeData.push({
      name: "Contact",
      type: "update",
      display: { type: "hoverLoader", model: "contacts" },
      previous: previous.contact?.id,
      current: current.contact?.id,
    })
  }

  if (previous.delivery_note_email !== current.delivery_note_email) {
    changeData.push({
      name: "Delivery Note Email",
      type: "update",
      previous: previous.delivery_note_email,
      current: current.delivery_note_email,
    })
  }

  if (previous.purchase_order_reference !== current.purchase_order_reference) {
    changeData.push({
      name: "PO Reference",
      type: "update",
      previous: previous.purchase_order_reference,
      current: current.purchase_order_reference,
    })
  }

  if (previous.delivery_method !== current.delivery_method) {
    changeData.push({
      name: "Delivery Method",
      type: "update",
      previous: previous.delivery_method,
      current: current.delivery_method,
    })
  }

  if (previous.dpd_delivery_info !== current.dpd_delivery_info) {
    changeData.push({
      name: "DPD Delivery Info",
      type: "update",
      previous: previous.dpd_delivery_info,
      current: current.dpd_delivery_info,
    })
  }

  if (previous.carriage_pennies !== current.carriage_pennies) {
    changeData.push({
      name: "Carriage",
      type: "update",
      previous: asPrice(previous.carriage_pennies, currency),
      current: asPrice(current.carriage_pennies, currency),
    })
  }

  if (previous.projectRegistrationNumber !== current.projectRegistrationNumber) {
    changeData.push({
      name: "Project Registration Number",
      type: "update",
      previous: previous.projectRegistrationNumber,
      current: current.projectRegistrationNumber,
    })
  }
  const commericalInvoiceAttrs = (
    ["origin", "weight", "dimensions", "pallet_size"] as const
  ).filter(v => previous?.[v] !== current?.[v])
  if (commericalInvoiceAttrs.length > 0) {
    changeData.push({
      name: "Commerical Invoice",
      type: "update",
      data: commericalInvoiceAttrs.map(v => ({
        name: titleize(v),
        type: "update" as const,
        previous: previous?.[v],
        current: current?.[v],
      })),
    })
  }

  if (JSON.stringify(previous.address) !== JSON.stringify(current.address)) {
    const addressDif = [
      "label",
      "name",
      "email",
      "phone",
      "contact",
      "line_1",
      "line_2",
      "line_3",
      "line_4",
      "postcode",
      "city",
      "county",
      "country",
    ]
      .filter(v => previous.address?.[v] !== current.address?.[v])
      .map(attr => ({
        name: titleize(attr),
        type: "update" as const,
        previous: previous.address?.[attr],
        current: current.address?.[attr],
      }))
    if (previous.address && !current.address) {
      changeData.push({
        name: "Address",
        type: "remove",
        data: [],
      })
    } else {
      changeData.push({
        name: "Address",
        type: "add",
        data: addressDif,
      })
    }
  }

  if (JSON.stringify(previous.billing_address) !== JSON.stringify(current.billing_address)) {
    const addressDif = [
      "label",
      "name",
      "email",
      "phone",
      "contact",
      "line_1",
      "line_2",
      "line_3",
      "line_4",
      "postcode",
      "city",
      "county",
      "country",
    ]
      .filter(v => previous.billing_address?.[v] !== current.billing_address?.[v])
      .map(attr => ({
        name: titleize(attr),
        type: "update" as const,
        previous: previous.billing_address?.[attr],
        current: current.billing_address?.[attr],
      }))
    if (previous.billing_address && !current.billing_address) {
      changeData.push({
        name: "Billing Address",
        type: "remove",
        data: [],
      })
    } else {
      changeData.push({
        name: "Billing Address",
        type: "add",
        data: addressDif,
      })
    }
  }

  if (previous.items && current.items) {
    const currentExistingItems = current.items.filter(v => !v.$deleted)
    const previousExistingItems = previous.items.filter(v => !v.$deleted)
    const newItems = currentExistingItems
      ?.filter(v => !previousExistingItems?.find(x => x.id === v.id))
      .map(item => ({ previous: null, current: item }))
    const removedItems = previousExistingItems
      ?.filter(v => !currentExistingItems?.find(x => x.id === v.id))
      .map(item => ({ previous: item, current: null }))
    const updatedItems = currentExistingItems
      ?.filter(v => previousExistingItems?.find(x => x.id === v.id))
      .map(item => {
        const prev = previousExistingItems?.find(x => x.id === item.id)
        return { previous: prev, current: item }
      })
    const createdItems = newItems
      .map(item => ({
        name: "Item",
        type: "add" as const,
        data: buildItemChange(item.previous, item.current),
      }))
      .filter(v => v.data.length > 0)
    const deletedItems = removedItems
      .map(item => ({
        name: `Item ${item.previous.stock_code}`,
        type: "remove" as const,
        previous: item.previous.stock_code,
        current: null,
      }))
      .filter(v => !!v.previous)
    const altered = updatedItems
      .map(item => ({
        name: `Item ${item.previous?.stock_code}`,
        type: "update" as const,
        data: buildItemChange(item.previous, item.current),
      }))
      .filter(v => v.data.length > 0)
    changeData.push(...createdItems, ...deletedItems, ...altered)
  }
  return changeData
}

export type IAutoExportOrderAcknowledgementOn = "onExport" | "onPick" | "onDelivery"

export function AutoSendOrderAcknowledgementCheckbox({
  onChange,
  value,
  label,
  disabledOptions,
  disabled,
  disable_barscan,
}: {
  onChange: (value?: IAutoExportOrderAcknowledgementOn) => void
  value?: string
  label?: string
  disabledOptions?: { key: IAutoExportOrderAcknowledgementOn; tooltip?: string }[]
  disabled?: boolean
  disable_barscan?: boolean
}) {
  const { barScanOrders } = useSettings()

  function optionIsDisabled(opt: IAutoExportOrderAcknowledgementOn) {
    return disabledOptions?.find(x => x.key === opt)
  }

  return (
    <FieldsRadioButtonSelect
      label={label}
      onChange={(value: any) => onChange(value)}
      options={[
        barScanOrders &&
          !disable_barscan && {
            label: "On Export",
            value: "onExport",
            disabled: disabled || !!optionIsDisabled("onExport"),
            tooltip: optionIsDisabled("onExport")?.tooltip,
          },
        {
          label: "On Pick",
          value: "onPick",
          disabled: disabled || !!optionIsDisabled("onPick"),
          tooltip: optionIsDisabled("onPick")?.tooltip,
        },
        (!barScanOrders || disable_barscan) && {
          label: "On Dispatch",
          value: "onDelivery",
          disabled: disabled || !!optionIsDisabled("onDelivery"),
          tooltip: optionIsDisabled("onDelivery")?.tooltip,
        },
      ].filter(Boolean)}
      value={value}
    />
  )
}

async function runCreditLimitCheck(
  utils: {
    skeem: IWebClient
    events: ReturnType<typeof useEvents>
  },
  args: {
    orderId: string
    customerId: string
    recordId: string
  },
) {
  const { events, skeem } = utils
  const { customerId, orderId, recordId } = args
  // get required customer attrs
  const customer = await skeem.fetch2<
    IRecord<"customers", "tax_rate" | "outstanding_balance_pennies" | "credit_limit_pennies"> & {
      branch: IRecord<"branches", "currency_name">
    }
  >({
    first: {
      name: "customers",
      attributes: [
        "tax_rate",
        "outstanding_balance_pennies",
        "credit_limit_pennies",
        { name: "branch", attributes: ["currency_name"] },
      ],
      filter: { matches: { id: customerId } },
    },
  })
  const limit = customer.credit_limit_pennies ?? 0
  const outstanding = customer.outstanding_balance_pennies ?? 0
  // only bother doing checks if we have assigned a limit
  if (limit) {
    const customerOrder = await skeem.fetch2<
      IRecord<"orders", "carriage_pennies"> & {
        items: IRecord<
          "order_items",
          "price_pennies" | "quantity" | "discountPercent" | "product"
        >[]
      }
    >({
      first: {
        name: "orders",
        attributes: [
          "carriage_pennies",
          {
            name: "items",
            attributes: ["price_pennies", "quantity", "discountPercent", "product"],
          },
        ],
        matches: { id: orderId },
      },
    })

    // value of the current order we just saved.
    const orderValue = getTotals(
      (customerOrder?.items as any) ?? [],
      customerOrder?.carriage_pennies ?? 0,
      customer?.tax_rate,
    )?.totalPricePennies

    const currencyName = customer.branch.currency_name ?? ""
    const currency = currencyNameToSymbol(currencyName)

    const outstandingWithOrder = outstanding + orderValue

    // current outstanding is < limit, but with our order will go over
    if (outstandingWithOrder > limit) {
      await events.creditLimitExceeded(customer.id, {
        creditLimit: customer.credit_limit_pennies ?? 0,
        outstanding: outstandingWithOrder,
        amountChanged: orderValue,
        resourceId: recordId,
        resourceType: "orders",
        currency,
      })
    }
  }
}
